/* ============================================================
   JUCE 3.0 - juce-loom.js
   The LOOM tracker. What happens on the storefront - visits, product looks,
   carts, chats, the taps in between - is queued here and posted to the
   JENNIE server, which weaves it into one thread per person. The pinned
   contract is jennie-buildout/docs/LOOM.md: section 5 is this file's spec,
   section 4 is the wire shape it sends.

   HOW IT WATCHES WITHOUT TOUCHING. No feature code knows this file exists:
   every hook is a delegated document-level listener on the theme's stable
   data-* seams, bound in the CAPTURE phase so a feature handler stopping
   propagation cannot blind the record, and passive so watching never holds
   up a tap. The one exception is a single wrap around window.fetch - the
   only honest way to know an add actually LANDED in the cart is to watch
   /cart/add.js answer - and the wrap passes every request through
   untouched, on success and error alike.

   THE RULES: 1. Capture never breaks the shop (LOOM.md, law 1) - every
   handler swallows its own errors, a lost event is always preferred to a
   surfaced one. 2. A prerendered page is not a visit - nothing runs, not
   even a storage write, until the document is genuinely the shopper's own.
   3. No means no - when window.theme.loom is absent (the dial is off) or
   the browser sends doNotTrack / globalPrivacyControl, this module writes
   nothing, listens to nothing, and leaves only an inert window.loom so
   feature code calling the API stays safe.

   4. NOTHING IS STORED AND NOTHING IS SENT BEFORE A YES. The gate
   (juce-consent.js) owns the question; this file only ever reads the answer,
   synchronously, out of the record the gate wrote. Until analytics is
   granted, three functions - writeState, mirror and flush - are no-ops, and
   those three are the only places this module touches disk or network. Every
   other line runs exactly as it always did.

   WHICH IS WHY THE PAGE THE DECISION HAPPENS ON IS NOT LOST. The queue is
   already an in-memory array that only reaches localStorage through mirror(),
   so with mirror and flush closed it becomes precisely the buffer this needs:
   real events with their real timestamps, held in the page and nowhere else.
   Somebody who lands, looks at a scent and then presses Allow has the landing,
   the referrer, the campaign and the product view all recorded - lawfully,
   because nothing was processed until they agreed. No separate buffer was
   written for this; the honest one was already here.

   IT DOES NOT SURVIVE A NAVIGATION, and that is a decision rather than a
   limitation. Carrying it across pages would mean writing it to session or
   local storage, which is the exact act the banner promises has not happened
   yet - so a shopper who browses three pages before answering is measured
   from the third. The cost is real and it is paid deliberately: the banner is
   deliberately quick (900ms) and one tap wide precisely because the honest
   version of this cannot buy that visit back later.

   Personalisation is the SECOND grant and it gates identity, not capture:
   without it the envelope carries no claim and no jennie id, so the journey
   is recorded without ever being stitched to a person.
   ============================================================ */

const conf = (window.theme && window.theme.loom) || null;
/* THE VERSION OF THIS RENDER (snippets/release-version.liquid via
   layout/theme.liquid), shape-checked so a hand-edited config cannot ride a
   paragraph onto a column. Every event is stamped with it as it is queued
   (`r`): a batch left in the queue across a release then lands on the
   version it was recorded on, not the one it was flushed from
   (steering/house/31-theme-versions.md section 4). '' on an older render. */
const PAGE_REL = (conf && typeof conf.rel === 'string' && /^\d{1,4}\.\d{1,4}\.\d{1,4}$/.test(conf.rel)) ? conf.rel : '';

/* Either privacy signal is a plain no, and the answer to a plain no is
   NOTHING: no anon id minted, no listener bound, no byte sent. */
/* The same test juce-consent.js makes (2026-09-14): it counted 'yes' and
   window.doNotTrack as a signal and hid the banner, while this file did not
   and went on counting and pinging the wall for the same browser. */
const refused = navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes'
  || window.doNotTrack === '1' || !!navigator.globalPrivacyControl;

/* WHAT THE SHOPPER ANSWERED, READ HERE RATHER THAN ASKED FOR.
   juce-consent.js is loaded first and would normally have published
   window.juceConsent by now, but this module does not depend on that: it
   parses the same first-party record itself, synchronously, so a gate that
   failed to parse or arrived late leaves LOOM silent instead of leaving it
   guessing. Deliberately a copy of eight lines rather than an import - the
   direction of a mistake here matters more than the duplication.

   The cookie is the elder of the two stores (it survives Safari's seven-day
   script-storage cap, which is the same reason the visitor id is mirrored
   into one), and the localStorage mirror is the fallback for a browser that
   refused the cookie. Shape: "1.<decided at, seconds>.<policy>.<grants>",
   grants being letters from {a,p,m} or a lone "-" for a recorded refusal. */
function readGrant() {
  let raw = null;
  try {
    const m = document.cookie.match(/(?:^|;\s*)juce_consent=([^;]+)/);
    if (m) raw = decodeURIComponent(m[1]);
  } catch (err) { /* fall through to the mirror */ }
  if (!raw) {
    try {
      const mr = JSON.parse(localStorage.getItem('juce:consent:v1'));
      if (mr && mr.at) {
        raw = '1.' + mr.at + '.' + mr.policy + '.'
          + ((mr.grants || []).map((g) => g.charAt(0)).join('') || '-');
      }
    } catch (err) { /* nothing stored */ }
  }
  if (!raw) {
    /* MEASURED ON NOTICE (owner, 2026-09-14). No answer on record, and this
       tab's banner has already been recorded server-side as the notice
       (juce-consent.js NOTICE, core/consent.ts recordNotice): analytics runs,
       nothing that names anybody does. Without that session key this is the
       old silence, and the buffer waits for juce-consent.js to say so. */
    try {
      const n = JSON.parse(sessionStorage.getItem('juce:notice:v1'));
      if (n && Array.isArray(n.grants) && n.grants.indexOf('analytics') !== -1 && typeof n.vid === 'string') {
        return { analytics: true, personal: false, decided: false };
      }
    } catch (err) { /* no session storage: wait for the event */ }
    return { analytics: false, personal: false, decided: false };
  }
  const parts = String(raw).split('.');
  if (parts.length < 4 || parts[0] !== '1') return { analytics: false, personal: false, decided: false };
  const grants = parts[3];
  /* `decided` is the third fact, and I5 is why it now exists. A refusal and a
     shopper who has simply not been asked yet are the same silence to the
     three chokepoints, and they must NOT be the same thing to the counter
     below: an unanswered visit is still buffered in memory waiting for a yes.
     Since 2026-09-11 both are counted, in aggregate, under separate lanes -
     and the unanswered tally waits for the way out of the page so a yes can
     still drop it, which is what keeps one visit out of two places (laneNow
     in start()). */
  return {
    analytics: grants.indexOf('a') !== -1,
    personal: grants.indexOf('p') !== -1,
    decided: true
  };
}

/* NOT EVERY HIT IS A SHOPPER, AND A RECORD THAT CANNOT TELL IS NOT A RECORD.
   Nothing here filtered anything: a crawler walking the catalogue, a
   Lighthouse run, the theme editor redrawing a section on every keystroke and
   a preview link shared around all landed in loom_events as journeys, with
   sessions, dwell times and funnel steps of their own. That is not a small
   skew. Search engines re-crawl this shop constantly, every crawl is a
   land -> browse -> leave route drawn on the map, and the bounce rate on the
   analytics page is an average that includes them.

   Three separate questions, because they fail in different ways:

   1. IS THERE A PERSON. A headless browser announces itself in the user agent
      or in navigator.webdriver, and the list below is deliberately short and
      conservative: it names crawlers and automation only. In-app browsers are
      P0 for this store (a WhatsApp or Instagram link is how a great many of
      these pages are opened) and their user agents carry the app's name - so
      no app name appears here. A missed bot costs one phantom session; a
      matched shopper costs a real one, and that trade is not symmetrical.

   2. IS THIS THE LIVE SHOP. Shopify.theme.role is 'main' on the published
      theme and something else on every preview and development copy, which is
      the one signal that does not depend on a URL keeping its parameters.

   3. IS SOMEBODY EDITING IT. Shopify.designMode is true inside the theme
      editor, where a section re-render is not a page view and a click on a
      block is not a shopper's click.

   Refusing HERE rather than filtering server-side is the whole point: the
   inert window.loom means nothing is minted, nothing is stored and nothing is
   sent, so a crawl leaves no cookie, no localStorage and no row. */
const UA = navigator.userAgent || '';
const AUTOMATED = /bot\b|bots\b|crawler|crawling|spider|scraper|slurp|headless|lighthouse|pagespeed|speedcurve|pingdom|gtmetrix|ahrefs|semrush|dataprovider|facebookexternalhit|embedly|curl\/|wget|python-requests|node-fetch|axios\/|phantomjs|puppeteer|playwright|selenium|google web preview|google-inspectiontool/i;
const shop = window.Shopify || {};
/* ---- THE PRE-LAUNCH EXCEPTION ----
   (owner, 2026-08-22: "when selecting filters and searching terms does not
   record on the customer profile ... I did filters and searched apple, yet it
   still did not record it on my profile".)

   Nothing downstream was broken and nothing was dropped: it never left the
   browser. JUCE 3.0 is not the published theme yet, so rule 2 was refusing
   every event on the very theme the shop is being built in - window.loom was
   the inert stub, and no filter, no search and no product view has ever been
   minted here. A shelf that records nothing looks identical to a fold that is
   running late, which is why this cost an afternoon to see.

   The rule stays: a preview link shared around must not draw journeys on the
   map. It stops being unconditional. `loom.preview` - off unless somebody
   turns it on, and set in the theme editor - is the shop saying "this copy is
   the one we are working in, record it".

   The other two questions are NOT negotiable by a setting. designMode is the
   theme editor redrawing a section on every keystroke, and a bot is a bot
   whatever the settings say. And the day this theme is published the flag
   stops mattering entirely, because role is 'main' and rule 2 never fires. */
const previewOk = !!(conf && conf.preview);
const synthetic = AUTOMATED.test(UA)
  || navigator.webdriver === true
  || shop.designMode === true
  || !!(shop.theme && shop.theme.role && shop.theme.role !== 'main' && !previewOk);

/* THE WIDTH THE LAYOUT IS ACTUALLY IN, and it is documentElement.clientWidth
   rather than window.innerWidth on purpose.

   The bucket exists to answer "which layout is this", and what decides that is
   the media queries - so the number it is computed from has to be the number
   the media queries are evaluated against. Those two are usually the same and
   are not always: they differ by the width of a classic scrollbar, they differ
   under browser zoom, and they differ completely under a device-metrics
   override, where innerWidth was seen reporting 1290 on a viewport that every
   media query in the page agreed was 414. A press recorded in that state is
   filed against a layout it was not made in, which is the one mistake this
   column exists to prevent.

   assets/juce-heatmap.js carries an identical copy and must keep it: the two
   have to agree about what bucket a page is in or heat is recorded under one
   layout and drawn over another. */
const viewportWidth = () => {
  const d = document.documentElement;
  return (d && d.clientWidth) || window.innerWidth || 0;
};
const bucketOf = (w) => (w < 768 ? 's' : w < 1280 ? 'm' : 'l');

/* ================== RESOLVING A HEAT ANCHOR: ONE COPY ==================
   An anchor is `surface/how/name/index` (or a bare surface), built by
   anchorOf() when a press is recorded and read back by the viewer when it is
   drawn. Two halves of one format, in two files, a year apart in when they
   run - which is exactly the shape of the LOOM_BROWSE_NAMES bug: the server
   gained `filter.apply`, the dashboard's private copy of the same list did
   not, and an unladdered name maps to null and is simply NOT DRAWN. No error,
   no warning, just a picture quietly missing a step.

   So there is ONE copy and it lives out here, above every consumer, and the
   viewer is HANDED it rather than writing its own. A tracker that learns a
   new kind of surface teaches the viewer in the same edit, because there is
   nowhere else for the knowledge to be.

   Out here rather than inside start() for the second reason too: start() does
   not run under Do Not Track, and looking at last week is not tracking. */
function heatSelector(how, name) {
  if (how === 'd') return '[data-loom-cta="' + name + '"]';
  if (how === 'h') {
    /* `fx-pick:character` is the hook data-fx-pick WITH the value character -
       see hookVal() below for why the value rides along. */
    const i = name.indexOf(':');
    if (i > 0) return '[data-' + name.slice(0, i) + '="' + name.slice(i + 1) + '"]';
    return '[data-' + name + ']';
  }
  return name; /* shape names are already CSS: tag, tag#id, tag.class */
}

/* THE HOOK'S VALUE, WHEN IT IS KEY-SHAPED, IS PART OF THE PLACE.
   (owner, 2026-09-02: the heatmap "needs to be smart enough to handle
   visible on things like the scent finder, layering and such dynamically and
   automatically so if we change it it will automatically detect that".)

   The finder renders one pick per answer across five panels, every one of
   them `data-fx-pick="<question>"`; the duet's quiz BUILDS its options in JS,
   every one `data-dt-qpick="<question>"`. Named by the hook alone, a press on
   the third character pick was "fx-pick, the 14th one on the page" - true
   today and wrong the day a panel gains an option or the quiz draws a
   different question first. Named `fx-pick:character`, the index counts the
   character picks only, so a reorder elsewhere on the page moves nothing and
   the viewer's selector finds the same control by what it IS.

   Only a value that could be a key qualifies: lowercase, short, the same
   alphabet as a name, not a boolean, not a three-digit id (NOISY). A label
   or a handle with a space stays out, and the anchor falls back to the bare
   hook exactly as before. The CTA subject (x.cta.click) does NOT carry the
   value - that column has a history and its vocabulary stays put. */
const HOOK_VAL_RE = /^[a-z0-9][a-z0-9._-]{0,23}$/;
const HOOK_VAL_SKIP = /^(?:true|false|1|0|on|off|yes|no|null|undefined|none)$/;
function hookVal(raw) {
  const v = String(raw == null ? '' : raw).trim().toLowerCase();
  if (!v || !HOOK_VAL_RE.test(v) || HOOK_VAL_SKIP.test(v)) return '';
  if (/\d{3}/.test(v)) return '';
  return v;
}

/* The selector behind each landmark surface - surfaceOf()'s rule 3, kept in
   one place so the three readers below cannot drift from it. */
const SURFACE_SEL = {
  drawer: '[data-drawer]',
  modal: 'dialog, [role="dialog"]',
  sheet: '[data-menu], [data-sheet]',
  dock: '[data-dock]',
  header: 'header, [data-header]',
  footer: 'footer',
};
const sectionSel = (sec) =>
  '[data-loom-surface="' + sec + '"], [id="shopify-section-' + sec + '"],'
  + ' [id^="shopify-section-"][id$="__' + sec + '"]';

/* EVERY element a surface name can refer to, in document order. A surface is
   usually one element, and `modal` is not: it is every <dialog> on the page -
   the cart drawer, the menu sheet, the search popup, the chat - and a press
   recorded inside the menu sheet was filed under `modal` because the sheet IS
   a dialog. The first version answered with the first dialog in the DOM,
   which on the real theme is the drawer, so no press in the sheet could ever
   be found and every one of them fell to the depth fallback down the middle
   of the page. (2026-09-02, seen on the live phone layout.) */
function surfaceRoots(sec) {
  try {
    if (!sec || sec === '-') return [];
    const sel = SURFACE_SEL[sec] || sectionSel(sec);
    return Array.prototype.slice.call(document.querySelectorAll(sel));
  } catch (err) { return []; }
}
function surfaceRoot(sec) {
  const all = surfaceRoots(sec);
  return all.length ? all[0] : null;
}
/* The surface root that CONTAINS an element - the one the index must be
   counted in, because it is the one the press happened in. */
function surfaceRootOf(sec, el) {
  try {
    if (!sec || sec === '-' || !el || !el.closest) return null;
    return el.closest(SURFACE_SEL[sec] || sectionSel(sec));
  } catch (err) { return null; }
}

/* THE ONE READER OF THE ANCHOR FORMAT. Returns the live element a recorded
   press was measured against, or null when the page no longer has it - which
   is a real and useful answer, not a failure: the viewer counts those and
   falls back to the press's document position, and says how many it had to.

   Every candidate root is tried in order and the first that holds a match at
   the index wins. For a bare surface an OPEN dialog is preferred, because a
   backdrop tap on the sheet belongs to the sheet and not to the drawer. */
function resolveAnchor(anchor) {
  try {
    if (!anchor) return null;
    const parts = String(anchor).split('/');
    if (parts.length === 1) {
      const roots = surfaceRoots(parts[0]);
      for (const r of roots) { try { if (r.matches('[open]')) return r; } catch (err) { /* fine */ } }
      return roots.length ? roots[0] : null;
    }
    if (parts.length !== 4) return null;
    const i = Number(parts[3]);
    if (!Number.isInteger(i) || i < 0) return null;
    const sel = heatSelector(parts[1], parts[2]);
    const roots = surfaceRoots(parts[0]);
    if (!roots.length) roots.push(document);
    for (const root of roots) {
      const all = root.querySelectorAll(sel);
      if (i < all.length) return all[i];
    }
    return null;
  } catch (err) { return null; }
}

/* ===================== THE HEATMAP VIEWER'S DOOR =====================
   (owner, 2026-09-02: "note that this module will never really load any js
   unless the person uses the special link".)

   THIS IS THE ENTIRE COST OF THE FEATURE ON A SHOPPER'S PAGE: one substring
   test against location.hash, and one sessionStorage read that only happens
   if the first one has ever passed in this tab. No asset is requested, no
   listener is bound, no observer is constructed and nothing is drawn. The
   viewer - which is a canvas renderer, a widget, a date picker and a network
   client, and has no business anywhere near a shopper - lives in its own file
   and is fetched by a dynamic import() that this branch is the only caller
   of. A shop that never opens a heat link never downloads a byte of it.

   IT LIVES OUT HERE, ABOVE THE TRACKER'S OWN BOOT, ON PURPOSE. Three of the
   conditions below this comment turn the tracker into a stub: a Do Not Track
   header, a preview theme, a webdriver. Every one of those is a thing a
   member of staff plausibly has switched on, and none of them has anything to
   say about whether that person may LOOK at a picture of last week. Reading
   an aggregate is not tracking; putting the gate inside start() would have
   meant the one colleague who runs GPC could never open the tool, and it
   would have taken somebody an afternoon to work out why.

   THE TOKEN RIDES THE FRAGMENT, NEVER THE QUERY STRING. A fragment is not
   sent to any server: it stays out of Shopify's access log, out of the
   Referer header on the way to any third party, and out of every analytics
   pipeline the storefront carries. It is moved into sessionStorage on arrival
   and wiped from the address bar in the same breath, so that walking the shop
   with the overlay open does not leave a working capability in the history of
   a browser, in a screenshot, or in a link somebody helpfully shares. */
const HEAT_KEY = 'juce:heat:v1';
const HEAT_RE = /(?:^|[#&?])heat=([A-Za-z0-9_-]{32,128})(?:&|$)/;
/* The window the link was minted with, so the walk opens on the dates
   somebody was already reading rather than on a default. Shape-checked here
   rather than trusted: these two go into a query string the server parses as
   days, and the fragment is the one part of a URL anybody can edit without
   leaving a trace anywhere. */
const HEAT_DAY_RE = /[#&](from|to)=(\d{4}-\d{2}-\d{2})(?:&|$)/g;
/* Which layout the link was made for. The viewer resizes the window to it
   on arrival, because a phone's heat over a desktop layout is a wrong
   picture that looks like a right one (assets/juce-heatmap.js, the layout
   switch). One letter, closed set, so nothing can ride in on it. */
const HEAT_W_RE = /[#&]w=([sml])(?:&|$)/;
/* Which versions the link was minted for (docs/RELEASES.md): a version the
   ledger knows, and which of the seven ways it is read (line, layout, cut,
   page, since, only, all). Closed shapes, so nothing can ride in on either;
   the + of an editor version may arrive percent-encoded. */
const HEAT_REL_RE = /[#&]rel=(\d{1,4}\.\d{1,4}\.\d{1,4}(?:(?:%2B|\+)e\d{1,4})?)(?:&|$)/;
const HEAT_RELMODE_RE = /[#&]relMode=(line|layout|cut|page|since|only|all)(?:&|$)/;
let heatViewing = '';
let heatWindow = {};
try {
  const m = HEAT_RE.exec(location.hash || '');
  if (m) {
    heatViewing = m[1];
    let d;
    HEAT_DAY_RE.lastIndex = 0;
    while ((d = HEAT_DAY_RE.exec(location.hash || ''))) heatWindow[d[1]] = d[2];
    const wm = HEAT_W_RE.exec(location.hash || '');
    if (wm) heatWindow.w = wm[1];
    const rm = HEAT_REL_RE.exec(location.hash || '');
    if (rm) heatWindow.rel = rm[1].replace('%2B', '+');
    const mm = HEAT_RELMODE_RE.exec(location.hash || '');
    if (mm) heatWindow.relMode = mm[1];
    try { sessionStorage.setItem(HEAT_KEY, heatViewing); } catch (err) { /* law 1 */ }
    /* Out of the address bar, without adding a history entry to back out of. */
    try {
      const clean = (location.hash || '').replace(HEAT_RE, '').replace(/^[#&]+|&+$/g, '');
      history.replaceState(null, '', location.pathname + location.search + (clean ? '#' + clean : ''));
    } catch (err) { /* law 1 */ }
  } else {
    /* The second and every later page of the walk. Only reached in a tab that
       arrived on a heat link, because sessionStorage is per-tab and nothing
       else ever writes this key. */
    try { heatViewing = sessionStorage.getItem(HEAT_KEY) || ''; } catch (err) { heatViewing = ''; }
    if (heatViewing && !HEAT_RE.test('#heat=' + heatViewing)) heatViewing = '';
  }
} catch (err) { heatViewing = ''; }

if (heatViewing) {
  /* THE THEME'S OWN asset_url FIRST, a sibling of this file second.
     `conf.viewer` is what layout/theme.liquid renders, and it is the only
     form that carries the ?v= content digest - Shopify's CDN will serve the
     file without one, but then the URL never changes, so a browser that has
     opened the viewer once is holding that copy until its cache decides
     otherwise. The sibling resolve stays as the fallback because this file
     outlives any one render of the layout: a page cached from before the
     setting existed must still be able to open the tool. */
  try {
    const src = (conf && typeof conf.viewer === 'string' && conf.viewer)
      ? conf.viewer
      : new URL('juce-heatmap.js', import.meta.url).href;
    import(src)
      .then((mod) => {
        if (mod && typeof mod.openHeatmap === 'function') {
          mod.openHeatmap({
            token: heatViewing,
            endpoint: String((conf && conf.endpoint) || '').replace(/\/+$/, ''),
            key: HEAT_KEY,
            from: heatWindow.from,
            to: heatWindow.to,
            w: heatWindow.w,
            rel: heatWindow.rel,
            relMode: heatWindow.relMode,
            /* What THIS render was cut from, so the panel can say which
               version the page in front of you is. */
            pageRel: (conf && typeof conf.rel === 'string') ? conf.rel : '',
            /* Handed over rather than reimplemented - see the block above. */
            resolveAnchor: resolveAnchor
          });
        }
      })
      .catch(() => { /* law 1: a viewer that will not load is not the shop's problem */ });
  } catch (err) { /* law 1 */ }
}

/* ---- THE PASS ----
   `?jp=` is a token Jennie put on a link she sent somewhere we cannot see
   into: a WhatsApp reply, an Instagram DM, an email. The browser now holding
   it is almost certainly the person on the other end of that conversation,
   and handing the token back on the next batch is what joins the two
   (jennie-buildout/docs/PASS.md).

   READ AND ERASED IN THE SAME BREATH, and the erase is the part worth
   explaining. It runs before the boot gate, outside every consent branch, and
   it happens even when the answer was no, because it is not collection - it
   is tidying up after ourselves:

     - the address bar stops showing a token, so a screenshot, a shared URL or
       a link somebody pastes into a group chat carries nothing.
     - a reload cannot present the same pass twice.
     - it never reaches an analytics prop by accident. safeHref() already
       strips the whole query string, but a URL that never carries the token
       past the first tick cannot be leaked by anything written later.

   HELD IN MEMORY AND NOWHERE ELSE. Writing it to storage would be the exact
   write the consent banner promises has not happened yet, so a shopper who
   navigates before answering simply loses it - the same price the pre-consent
   event buffer already pays, and paid for the same reason. */
var PASS = null;
try {
  var m = /[?&]jp=([A-Za-z0-9_-]{8,24})\b/.exec(location.search);
  if (m) {
    PASS = m[1];
    var clean = new URL(location.href);
    clean.searchParams.delete('jp');
    /* replaceState, never assign: this must not be a navigation, must not add
       a history entry, and must not cost a paint. */
    history.replaceState(history.state, '', clean.pathname + clean.search + clean.hash);
  }
} catch (err) { PASS = PASS || null; }

if (!conf || !conf.endpoint || refused || synthetic) {
  window.loom = { track() {}, page() {}, identify() {} };
} else if (document.prerendering) {
  /* The whenActive idea from theme.js: a hover-prerendered PDP must not show
     up as a visit, so the whole boot waits for the page to become real. API
     calls made in the meantime are buffered in memory and replayed on
     activation - an event a page script logged during prerender becomes true
     the moment the page does. */
  const pending = [];
  const hold = (call) => { if (pending.length < 50) pending.push(call); };
  window.loom = {
    track(name, props) { hold(['track', name, props]); },
    page() { hold(['page']); },
    identify(claim) { hold(['identify', claim]); }
  };
  document.addEventListener('prerenderingchange', () => boot(pending), { once: true });
} else {
  boot([]);
}

function boot(pending) {
  try { start(pending || []); }
  catch (err) {
    /* Law 1 - and even a failed boot leaves a safe surface behind. */
    if (!window.loom) window.loom = { track() {}, page() {}, identify() {} };
  }
}

function start(pending) {
  const ENDPOINT = String(conf.endpoint).replace(/\/+$/, '') + '/a/e';
  /* THE COUNTER'S OWN DOOR. Deliberately not /a/e: that endpoint reads and
     writes the juce_vid cookie and stores rows keyed on a visitor, and the
     whole point of this lane is that neither happens. Two doors so the
     difference is a route in an access log rather than a flag in a body. */
  const ANON_ENDPOINT = String(conf.endpoint).replace(/\/+$/, '') + '/a/n';
  const STORE = 'juce:loom:v1'; /* {aid, sid, sat, attr} - who this visitor is */
  const QKEY = 'juce:loom:q';   /* the mirrored queue - a closed tab loses nothing */
  const GAP_MS = 30 * 60 * 1000;             /* GA4 session gap; the server's rule is the truth */
  const FIRST_MS = 90 * 24 * 60 * 60 * 1000; /* visitor first-touch window */
  const CAP = 100;
  const FLUSH_AT = 10;
  const FLUSH_MS = 5000;
  /* A PRESS, A PAGE, AN ADD OR A SEARCH LEAVES WITHIN ABOUT A SECOND
     (2026-10-22, owner: the Live view's watch and the CRM's Stalk follow a
     shopper as it happens, "well optimised and accurate"). The five-second
     tick stays for everything else, and a burst inside the second is still
     one request: this only stops a lone press waiting for the tick. */
  const SOON_MS = 1200;
  const SOON = { 'page.view': 1, 'x.heat.click': 1, 'x.heat.swipe': 1, 'cart.add': 1, 'search.query': 1, 'drawer.open': 1, 'menu.open': 1 };
  let soonTimer = 0;

  /* ---- THE GRANT ----
     One boolean each, read once at boot and moved only by the gate's event.
     `granted` closes the three doors below; `personal` decides whether the
     journey is ever attached to a person. Neither is ever set by this file
     from anything except a juce:consent event, and that event is only ever
     dispatched by a button somebody pressed. */
  let grant = readGrant();
  let granted = grant.analytics;
  let personal = grant.personal;
  /* THE THIRD STATE, and I5 (owner, 2026-08-25: keep an accurate picture of
     the customer "through other means as much as possible") is what it is
     for. `declined` is true only after a RECORDED no - Essential only, the
     X, or a withdrawal. It opens exactly one thing: an aggregate counter that
     mints no identifier, writes nothing to this device, sends no cookie and
     names nobody. See countAnon() and flushAnon() below for what that means
     line by line, and jennie-buildout/docs/CONSENT.md section 6b for the
     argument it stands on.

     It is FALSE while a decision is outstanding, and false under a privacy
     signal. Both of those are silence, and neither is a no we may act on. */
  let declined = grant.decided && !grant.analytics;

  /* ---- tiny helpers, jennie.js style: swallow, never throw ---- */
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID()
    /* v4-shaped fallback for museum browsers, so the server's uuid column
       never sees something it has to quarantine. */
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 3 | 8)).toString(16);
    }));
  const cap = (s, n) => (s == null ? undefined : String(s).slice(0, n));

  /* THE URL, MINUS ANYTHING THAT IS A PASSWORD.
     Shopify carries single-use credentials in the PATH:
     /account/reset/<id>/<token> and /account/activate/<id>/<token> each sign
     somebody in as that customer, and /orders/<token> opens a stranger's
     order status. This pixel boots on every page in the theme, so without
     this those strings would be written into loom_events.url and .path,
     copied into loom_sessions.entry_path, shown on the LOOM map, included in
     CSV exports and handed to the model in a session digest.
     Nothing downstream needs the token to know somebody was on the reset
     page, so it never leaves the browser. */
  function safeHref() {
    try {
      const u = new URL(location.href);
      u.search = '';
      u.hash = '';
      u.pathname = u.pathname
        .replace(/^\/account\/(reset|activate)\/[^/]+\/.*$/i, '/account/$1')
        .replace(/^\/orders\/[^/]+.*$/i, '/orders')
        .replace(/^\/checkouts\/.*$/i, '/checkouts');
      return u.href;
    } catch (err) { return location.pathname; }
  }
  const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : undefined; };

  /* Private mode or quota just means the thread forgets - never a throw. */
  function readState() {
    /* AN UNGRANTED VISIT STARTS FROM NOTHING, and takes the old rows with it.
       Anything already in these keys was written under a consent that has
       since lapsed, been answered no, or been withdrawn, so reading it back
       would quietly resurrect an identifier the shopper is entitled to have
       gone. The server does its own half of this on the withdrawal receipt;
       this is the browser's half, and it runs on every boot rather than only
       on the click that caused it. */
    if (!granted) { purge(); return {}; }
    try { return JSON.parse(localStorage.getItem(STORE)) || {}; } catch (err) { return {}; }
  }
  /* CHOKEPOINT 1 of 3. Everything above this line may run ungranted; nothing
     below it touches disk or network. The anon id still exists in memory so
     the queue can be built, and it is simply never written down. */
  function writeState() {
    if (!granted) return;
    try { localStorage.setItem(STORE, JSON.stringify(state)); } catch (err) { }
  }
  function purge() {
    try { localStorage.removeItem(STORE); } catch (err) { }
    try { localStorage.removeItem(QKEY); } catch (err) { }
    /* THE THIRD KEY, and it was being left behind (found 2026-08-25 while
       verifying I5, by reading localStorage after a refusal rather than by
       reading this function). 'juce:loom:ctx' holds a session id - it is the
       marker that stops the device-context bundle being sent twice in one
       session - so a withdrawal that removed the other two and not this one
       left a session identifier standing on the device of somebody who had
       just asked for their record to be deleted. Small, and exactly the kind
       of thing the panel's promise is measured by. */
    try { localStorage.removeItem('juce:loom:ctx'); } catch (err) { }
  }

  /* ---- identity ----
     The anon id is OURS and opaque - a UUID that says nothing to whoever
     reads it. The server mirrors it in the HttpOnly juce_vid cookie (which
     survives Safari's script-storage cap); when the two disagree the COOKIE
     wins, delivered as a 200 {vid} that flush() adopts below. The session
     id rotates after 30 minutes of silence, and is only ever a hint - the
     server re-derives sessions from the gap rule either way. */
  const now0 = Date.now();
  const state = readState();
  if (!state.aid) state.aid = uuid();
  if (!state.sid || now0 - (state.sat || 0) > GAP_MS) state.sid = uuid();
  state.sat = now0;

  /* ---- attribution ----
     Parsed once per real navigation (a bfcache restore is the SAME landing
     resumed). utm_* keys are stored stripped of their prefix and the ad
     click ids under their own names, the shape the wire contract shows.
     Session last-touch overwrites freely and rides every event; visitor
     first-touch is written once, respected for 90 days, and kept
     client-side only - the server derives its own first_touch. */
  const touch = (() => {
    try {
      const params = new URLSearchParams(location.search);
      const t = {};
      params.forEach((v, k) => {
        const key = k.toLowerCase();
        if (key.indexOf('utm_') === 0 && v) t[key.slice(4)] = cap(v, 100);
        else if (['gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid', 'ttclid', 'msclkid'].indexOf(key) !== -1 && v) t[key] = cap(v, 100);
      });
      return Object.keys(t).length ? t : null;
    } catch (err) { return null; }
  })();
  state.attr = state.attr || {};
  if (touch) {
    state.attr.last = { utm: touch, at: now0 };
    if (!state.attr.first || now0 - (state.attr.first.at || 0) > FIRST_MS) {
      state.attr.first = { utm: touch, at: now0 };
    }
  } else if (state.attr.last && now0 - (state.attr.last.at || 0) > GAP_MS) {
    /* LAST-TOUCH IS THE SESSION'S CAMPAIGN, NOT THE VISITOR'S. It used to be
       written once and then ride every event forever, so somebody who
       arrived from one Instagram story in March was still crediting that
       campaign with every direct visit in August - and the fold takes the
       session's utm from the first event that carries one, so every one of
       those sessions was filed under a dead campaign. GA4 semantics say a
       session ends at thirty minutes of silence and a new session with no
       tags is direct; that is now what happens. The visitor's ninety-day
       FIRST touch is untouched - that is a different question, and the
       server derives its own answer to it anyway. */
    delete state.attr.last;
  }
  writeState();

  /* ---- the queue ----
     In memory first, mirrored to localStorage on every change: a tab closed
     between flushes hands the leftovers to the next visit, and because
     every event carries a client-minted UUID the server dedupes, so
     at-least-once is safe. Capped at 100, OLDEST dropped - the newest
     events are the ones a journey can least afford to lose. (Two tabs
     share the mirror, last writer wins; the id dedupe also makes that
     shrug acceptable.) */
  let q = [];
  if (granted) {
    try {
      const kept = JSON.parse(localStorage.getItem(QKEY));
      if (Array.isArray(kept)) q = kept.slice(-CAP);
    } catch (err) { /* nothing kept */ }
  }

  /* CHOKEPOINT 2 of 3, and the one that turns the queue into the pre-consent
     buffer. Closed, `q` is an ordinary array in an ordinary page: it holds
     what happened, it dies with the tab or the navigation, and it has been
     processed by nobody. Opened, the same array is mirrored and flushed
     exactly as it always was - so the events already sitting in it when the
     shopper pressed Allow go out with the first batch rather than being
     thrown away for having happened forty seconds too early. */
  function mirror() {
    if (!granted) return;
    try { localStorage.setItem(QKEY, JSON.stringify(q)); } catch (err) { }
  }

  /* ---- the claim ----
     Who the shop thinks this is. A CLAIM, never a credential - the server
     files it as stated evidence only (attaches to empty shells, never
     fuses profiles). The signed-in customer object theme.liquid rendered
     always outranks whatever identify() stated - it came from the session,
     not from feature code, so its email wins and signed_in stays true. A
     stated claim only speaks for a browser with nobody signed in, and an
     anonymous browser sends no claim at all. */
  let stated = null;
  function claim() {
    const c = window.theme && window.theme.customer;
    if (c && c.signed_in) {
      return {
        email: c.email || (stated && stated.email) || null,
        signed_in: true,
        /* The Shopify customer id, when the theme rendered one. STILL a
           claim, not a credential - a curl can say anything - so the server
           files it exactly as stated evidence and the order webhook remains
           the verified join. What it buys is a deterministic HINT: a signed-
           in browser that has never ordered is recognisable the moment they
           land, instead of on their first purchase. */
        shopify_id: (c.id != null && /^[0-9]{1,20}$/.test(String(c.id)))
          ? String(c.id) : undefined,
        /* THE SIGNATURE that turns the claim into proof (2026-09-14).
           layout/theme.liquid signs 'juce.identity.v1.<id>.<email>' with a
           shop metafield only Shopify can read; the server recomputes it
           (core/storefront-identity.ts). Without it a signed-in shopper whose
           browser had been cleared, or who had chatted before signing in,
           was never tied back to their own profile. It proves exactly one
           account and is only sent under personalisation, like the claim. */
        proof: (typeof c.proof === 'string' && /^[0-9a-f]{64}$/i.test(c.proof))
          ? c.proof : undefined,
        first_name: cap(c.first_name, 80)
      };
    }
    return stated;
  }

  /* The widget's identity, read where jennie.js keeps it - localStorage
     first, its cookie second. Sending it as jid is what lets the server
     stitch "this browser" to "this chat person" deterministically. */
  function jennieId() {
    try {
      const s = JSON.parse(localStorage.getItem('juce:jennie:v1')) || {};
      if (s.jennie_id) return s.jennie_id;
    } catch (err) { /* fall through to the cookie */ }
    const m = document.cookie.match(/(?:^|;\s*)juce_jid=([0-9a-f-]{36})/i);
    return m ? m[1] : null;
  }

  /* ---- transport ----
     Flush when ten are waiting, every five seconds while any are (one
     timer, alive only while the queue is), and the moment the page hides.
     Hiding gets sendBeacon - a fetch can be killed with the page, a beacon
     is the browser's own promise to deliver; everything else gets fetch
     keepalive with credentials so the juce_vid cookie rides. Failure
     re-queues under the cap. */
  let timer = 0;
  let sending = false;
  let leaving = false;

  /* A BEACON HAS A CEILING AND THE QUEUE HAD NO IDEA OF IT. sendBeacon refuses
     any body over the browser's limit (64KB is the spec's floor and Safari's
     actual number) by returning false, and fetch keepalive shares one 64KB
     budget across every inflight request on the page. The old flush handed the
     WHOLE queue over as a single body and re-queued it on refusal - so a queue
     that grew past the limit while somebody was offline could never be sent
     again, and every later event was appended to a batch that was already too
     big to move. A permanently stuck queue is worse than a dropped event: it
     is a mirror in localStorage that never empties and a visit that never
     arrives, quietly, for as long as that browser lasts.

     So the queue leaves in bites it can fit through. 48KB leaves room for the
     envelope and for whatever else on the page is holding keepalive budget. */
  const WIRE_MAX = 48 * 1024;

  /* The next batch that fits, taken off the FRONT so order is kept. A single
     event too big to travel alone is dropped rather than left to wedge
     everything behind it - emit() caps the url at 300 and props at 1KB, so one
     cannot arrive through the front door; if one ever appears it is a bug
     upstream, and a stalled queue is a bad place to keep the evidence. */
  function takeChunk() {
    /* MEASURED AGAINST THE WHOLE BODY, NOT THE EVENTS. What travels is the
       envelope - the ids, the source, the claim - wrapped around the array, so
       budgeting for the array alone puts every batch over by however much the
       wrapper weighs. Asked rather than guessed, because the claim's size
       depends on who is signed in. */
    let room = WIRE_MAX;
    try { room -= JSON.stringify(envelope([])).length; } catch (err) { room -= 512; }
    const out = [];
    let size = 0;
    while (q.length) {
      let n;
      try { n = JSON.stringify(q[0]).length + 1; } catch (err) { n = room + 1; }
      if (n > room) { q.shift(); continue; }
      if (out.length && size + n > room) break;
      out.push(q.shift());
      size += n;
    }
    return out;
  }

  /* PERSONALISATION IS THE SECOND GRANT, and this is the only place it bites.
     Without it the batch still describes what happened - it simply never says
     who it happened to: no jennie id, so the widget's person is not welded to
     this browser, and no claim, so a signed-in session's email is not offered
     as stated evidence. The result is a real journey with nobody at the end
     of it, which is exactly what the panel promises ("she still helps, she
     just does not remember"). */
  function envelope(queued) {
    /* An event's own stamp rides only when it differs from the page's: a
       batch is one page almost always, so the wire stays as it was. A new
       array of copies, never the queue's own objects: a batch that fails is
       sent again, maybe from a page on another version, and must still
       carry the stamp it was recorded with. */
    const events = queued.map((e) => {
      if (!e || !('r' in e) || e.r !== PAGE_REL) return e;
      const c = Object.assign({}, e);
      delete c.r;
      return c;
    });
    const env = {
      v: 1, aid: state.aid, sid: state.sid,
      jid: personal ? jennieId() : null,
      source: 'theme', events
    };
    /* THE VERSION OF THE PAGE, one per envelope: what layout/theme.liquid
       rendered into loom.rel from snippets/release-version.liquid. Shape-
       checked so a hand-edited config cannot ride a paragraph onto a column;
       absent means an older render, which the server files as unstamped
       (jennie-buildout/docs/RELEASES.md). */
    if (PAGE_REL) env.rel = PAGE_REL;
    if (personal) {
      const c = claim();
      if (c) env.claim = c;
      /* THE PASS RIDES THE FIRST BATCH THAT LEAVES, and only under `personal`
         - it names a person by construction, so it belongs on the same side
         of the consent line as the claim and the jennie id.

         KEPT UNTIL A BATCH IS CONFIRMED SENT, not dropped the moment it is
         written onto one. The two failure modes are not symmetrical: a pass
         sent twice by the same browser is a no-op the server already answers
         with "yes, that one is yours" (core/pass.ts, the same-anon branch),
         while a pass dropped onto a batch that then failed its network call
         is a stitch that silently never happens. So it is cleared where
         success is actually known - see flush(). */
      if (PASS) env.tkt = PASS;
    }
    return env;
  }

  function requeue(batch) {
    q = batch.concat(q);
    if (q.length > CAP) q.splice(0, q.length - CAP);
    mirror();
  }

  /* CHOKEPOINT 3 of 3, and the only one that would leave the browser. Closed,
     a batch stays a batch: it is not dropped, not truncated and not retried,
     it simply waits, so the yes that opens this door finds the whole visit
     still standing behind it. */
  function flush(hiding) {
    try {
      if (!granted) return;
      if (!q.length) return;
      if (hiding) {
        /* AS MANY BEACONS AS THE QUEUE NEEDS, in order, stopping at the first
           refusal: a browser that has just said no to one body is not going to
           take the next one, and pushing past it would send the queue out of
           order. Whatever is left goes back, and the mirror hands it to the
           next visit exactly as it always did. */
        while (q.length) {
          const part = takeChunk();
          if (!part.length) break;
          mirror();
          /* text/plain ON PURPOSE, here and below: a JSON content type makes
             this a CORS-preflighted request, and a beacon cannot answer a
             preflight - it would silently never leave the browser. text/plain
             is a "simple" request, no preflight, and the server's /a/e parser
             reads the JSON out of it regardless (docs/LOOM.md section 4). */
          const body = new Blob([JSON.stringify(envelope(part))], { type: 'text/plain' });
          const took = !!(navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, body));
          if (!took) { requeue(part); return; }
          /* The browser has taken ownership of delivering it. That is the
             strongest promise this lane offers and it is where the pass is
             considered handed over. */
          PASS = null;
        }
        return;
      }
      if (sending) return;
      sending = true;
      const batch = takeChunk();
      if (!batch.length) { sending = false; return; }
      mirror();
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        /* Same text/plain trick as the beacon: a simple request skips the
           preflight round-trip entirely, and the cookie still rides. */
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(envelope(batch))
      }).then((res) => {
        sending = false;
        if (!res.ok) { requeue(batch); return; }
        /* Landed. Whatever the pass was going to do, it has now done. */
        PASS = null;
        /* A queue too big for one body drains over several ticks, so the timer
           has to stay armed for whatever did not fit in this one. */
        if (q.length) armTimer();
        /* 204 means stored and agreed. 200 carries {vid}: the juce_vid
           cookie disagrees with our localStorage copy (probably evicted
           and re-minted), the cookie is the elder - adopt its id. */
        if (res.status === 200) {
          res.json().then((j) => {
            if (j && typeof j.vid === 'string' && j.vid && j.vid !== state.aid) {
              state.aid = j.vid;
              writeState();
            }
          }).catch(() => { /* not JSON - nothing to adopt */ });
        }
      }).catch(() => { sending = false; requeue(batch); });
    } catch (err) { sending = false; }
  }

  function armTimer() {
    if (timer) return;
    timer = setInterval(() => {
      try {
        if (!q.length) { clearInterval(timer); timer = 0; return; }
        flush(false);
      } catch (err) { /* keep ticking */ }
    }, FLUSH_MS);
  }

  /* ---- building an event ----
     Every event: {id, at, name, url, ref?, utm?, subject?, variant_id?,
     value_cents?, props}. Lean on purpose: url capped at 300, subject at
     100, props dropped whole when their JSON tops ~1KB - a fat payload is
     a bug upstream, not a reason to fatten the wire. */
  function leanProps(p) {
    if (!p || typeof p !== 'object') return {};
    try {
      const s = JSON.stringify(p);
      if (!s || s.length > 1024) return {};
      return JSON.parse(s); /* the round trip also strips undefined values */
    } catch (err) { return {}; }
  }

  /* ================= THE DECLINED LANE (I5, 2026-08-25) =================

     WHAT THE OWNER ASKED FOR: after a no, still keep an accurate picture of
     the shop "through other means as much as possible". This is the version
     of that which is actually allowed, and the line it holds is worth writing
     down because it is easy to cross by accident.

     WHAT THIS LANE MAY NEVER DO, and none of it is a matter of taste:
       - mint, read, send or resurrect ANY identifier. No anon id, no session
         id, no jennie id, no juce_vid: the request goes out with
         credentials: 'omit', so the cookie this server would otherwise see is
         not sent, and a Set-Cookie coming back is not stored.
       - write anything to this device. Nothing to localStorage, nothing to
         sessionStorage, nothing to a cookie. The tally lives in a variable
         and dies with the tab.
       - fingerprint. No user agent, no screen, no timezone, no language, no
         IP-derived anything, no referrer, no utm, no query string, no title.
       - carry free text. A subject is a product handle matched against
         [a-z0-9-] or it is dropped, and a page is its TEMPLATE name (one of
         eight words), never its path.

     WHAT IS LEFT IS A COUNT: "on this day, the product template was viewed,
     and the subject was amber-noir". Nobody is in it. It cannot be joined to
     a person, to a session, or to another row in the same batch, because
     there is nothing in it to join on. That is what makes it lawful without
     consent - it is not personal information under POPIA s1, because there is
     no identifiable person for it to relate to - and it is also what makes it
     honest: the shop learns what it sells, and learns nothing about you.

     IT IS NOT LOOM AND IT NEVER BECOMES LOOM. It lands in its own table
     (loom_anon_daily) which no rollup, no journey, no funnel and no digest
     reads. A number from this lane may never be added to a loom_daily number:
     the two count different populations under different grounds, and a total
     of the pair would answer no question anybody asked.

     THE FIVE THINGS COUNTED are the ones that tell the shop what to stock and
     what to fix. Everything else - every plug-in event, every x.* name, every
     click - is dropped on the floor here. */
  const ANON_KINDS = {
    /* Not an event name (a public name is always filed under x.), so nothing
       else can count one: it is the presence lane saying a tab has started a
       visit without consent. See startHere(). */
    'visit': 'visit',
    'page.view': 'page',
    'product.view': 'product',
    'cart.add': 'add',
    'search.query': 'search',
    'checkout.start': 'checkout'
  };

  /* TWO SILENCES, ONE COUNTER (owner, 2026-09-11: "we still want to be able
     to see them and do basic essential tracking"). The lane above was opened
     for a RECORDED no only, and the commonest visitor the site has - somebody
     who never answered the banner at all - was counted nowhere. They are
     counted now, into the same identifier-free table, filed under 'unasked'
     so the two silences stay apart (150_loom_unmeasured.sql).

     What rule 5 of docs/CONSENT.md 6b was guarding against still holds, and
     is kept a different way: an unanswered visit is ALSO in the pre-consent
     buffer, waiting for a yes, and must not be filed twice. So an unasked
     tally is sent only on the way out of the page (never on the minute
     heartbeat), and a yes on the page drops it - the buffer carries those
     events into LOOM instead. The pages before a yes were never going to
     reach LOOM (the buffer dies with each navigation), so counting them here
     files them exactly once. */
  function laneNow() {
    if (granted) return null;
    return declined ? 'declined' : 'unasked';
  }
  /* A handle, or nothing. Anything with a slash, a query, an underscore, a
     capital or an id in it is not a handle and does not travel. */
  const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,79}$/;
  let tally = Object.create(null);
  let tallyN = 0;
  let anonTimer = 0;
  /* The tally may not grow without limit: a crawler or a scripted tab could
     otherwise turn it into a large body. 200 distinct keys is far more than a
     real visit produces - somebody who opens 200 different products in one
     session is not a shopper - and the 201st is dropped. */
  const ANON_KEYS = 200;

  function countAnon(name, subject, props) {
    try {
      const kind = ANON_KINDS[name];
      if (!kind) return;
      /* ONE ADD IS ONE ADD, ONE SEARCH IS ONE SEARCH. cart.add fires as
         intent on the submit and again when the cart confirms it; a search
         fires on every settled pause in the typing. The folds count only the
         confirmed add and the submitted or landed search, and so does this -
         until 2026-09-11 it counted both halves of every add. */
      if (kind === 'add' && props && props.intent) return;
      if (kind === 'search' && !(props && (props.submitted || props.landed))) return;
      let s = '';
      if (kind === 'page') {
        s = template();
      } else if (kind === 'product' || kind === 'add') {
        const h = String(subject || '').toLowerCase();
        s = HANDLE_RE.test(h) ? h : '';
      }
      const key = kind + '|' + s;
      if (tally[key] === undefined) {
        if (tallyN >= ANON_KEYS) return;
        tally[key] = 0;
        tallyN += 1;
      }
      tally[key] += 1;
      if (!anonTimer) {
        /* A slow heartbeat, not the event flush. A visit that never ends (a
           tab left open on a product page) still reports, and a visit that
           does end reports again on the way out. Sixty seconds is far enough
           from FLUSH_MS that the two lanes are never confused in a log. */
        anonTimer = setInterval(function () {
          try { flushAnon(false); } catch (err) { /* law 1 */ }
        }, 60000);
      }
    } catch (err) { /* law 1 */ }
  }

  function flushAnon(hiding, asLane) {
    try {
      const lane = asLane || laneNow();
      if (!lane || refused) { tally = Object.create(null); tallyN = 0; return; }
      /* An unanswered tally waits for the way out of the page, so a yes on
         this page can still drop it (laneNow's header). */
      if (lane === 'unasked' && !hiding && !asLane) return;
      const rows = [];
      for (const key in tally) {
        const cut = key.indexOf('|');
        rows.push({ k: key.slice(0, cut), s: key.slice(cut + 1), n: tally[key] });
      }
      if (!rows.length) return;
      /* CLEARED BEFORE THE SEND, and unlike flush() there is no requeue on
         failure. A dropped count is a slightly low number; a retried count
         held across a navigation is a queue, and a queue on this lane would be
         a record of a visit sitting on the device of somebody who said no. The
         cheaper mistake is the one that loses the number. */
      tally = Object.create(null);
      tallyN = 0;
      /* NO sendBeacon on this lane, ever: a beacon always carries cookies and
         cannot be told not to. keepalive fetch can, and 'omit' is the whole
         reason this is a separate function rather than a flag on flush(). */
      fetch(ANON_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ v: 1, lane: lane, rows: rows })
      }).catch(function () { /* law 1: a lost count is nobody's problem */ });
      if (hiding && anonTimer) { clearInterval(anonTimer); anonTimer = 0; }
    } catch (err) { /* law 1 */ }
  }

  /* ================= THE PRESENCE LANE (2026-09-11) =================

     WHAT THE OWNER ASKED FOR: the live wall "ignores people that is not
     consented, yet we still want to be able to see them". A browser that has
     not said yes - it has not answered the banner, or it said no - now tells
     the wall which room it is standing in, and nothing else.

     WHAT GOES: POST /a/h, credentials: 'omit', body {v, t, k, p, s} - a random
     tab id, one word for what just happened (page, product, add, search,
     checkout, leave, here, gone), the page's template word and a shop slug
     (a product, collection or page handle, matched against HANDLE_RE or left
     off). No cookie, no anon id, no url, no referrer, no campaign, no search
     words, no title, no device, no screen, no timezone.

     WHAT IS KEPT: nothing. The server folds the ping into the live wall's
     in-memory floor and writes no row of any kind (core/live-floor.ts,
     notePresence). When they leave the site they are gone from JENNIE.

     THE ONE THING WRITTEN TO THIS DEVICE is the tab id, in SESSION storage
     under juce:here:v1: it dies with the tab, it is never sent with a cookie,
     and it rotates after half an hour of silence like a session does. It is
     what lets the wall see one person walk from a shelf to a product rather
     than a stranger vanishing and another appearing, and it is what counts a
     visit once rather than once a page. It is named in the cookie notice
     (snippets/cookie-notice.liquid) with its answer to "does it leave the
     browser". Not under GPC or Do Not Track: under a privacy signal this
     whole file never boots.

     A YES ENDS IT. The tab says `gone`, the key is removed, and the same
     person arrives on the wall through the measured lane like everybody who
     agreed. */
  const HERE_ENDPOINT = String(conf.endpoint).replace(/\/+$/, '') + '/a/h';
  const HERE_KEY = 'juce:here:v1';
  const HERE_BEAT_MS = 45000;
  /* A reader counts as present while they have touched the page in the last
     five minutes; a tab left open overnight on a visible screen is not a
     person on the site, and the wall would never let them fade. */
  const HERE_IDLE_MS = 5 * 60000;
  let hereTok = null;
  let hereInput = Date.now();
  let hereTimer = 0;

  function hereToken() {
    const now = Date.now();
    if (!hereTok) {
      try {
        const raw = JSON.parse(sessionStorage.getItem(HERE_KEY) || 'null');
        if (raw && typeof raw.t === 'string' && raw.t.length === 36) hereTok = { t: raw.t, at: Number(raw.at) || 0 };
      } catch (err) { /* no storage: this page is its own visit */ }
    }
    if (!hereTok || now - hereTok.at > GAP_MS) {
      hereTok = { t: uuid(), at: now };
      /* A NEW VISIT WITHOUT CONSENT, counted once - into the same aggregate
         lane as everything else, so it is a number per day and nothing more. */
      countAnon('visit', '', null);
    }
    hereTok.at = now;
    try { sessionStorage.setItem(HERE_KEY, JSON.stringify(hereTok)); } catch (err) { /* memory only */ }
    return hereTok.t;
  }

  function pageSlug() {
    const m = location.pathname.match(/\/(?:products|collections|pages)\/([^/?#]+)/);
    const h = m ? String(m[1]).toLowerCase() : '';
    return HANDLE_RE.test(h) ? h : '';
  }

  function hereSend(body) {
    /* keepalive so a leave survives the page it is leaving; never a beacon,
       which always carries cookies and cannot be told not to. */
    fetch(HERE_ENDPOINT, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(body)
    }).catch(function () { /* law 1: the wall misses a beat */ });
  }

  /* THREE LANES ONTO THE WALL, ONE BEAT (2026-09-12, owner: "for LOOM we
     still want to track them as essentials tho, like shopify tho"). The same
     ask as 2026-09-11, which opened the identifier-free daily counts to
     people who refused and to people who never answered (150_loom_unmeasured
     .sql): the shop may know how many came through the door without knowing
     anything about any of them. That change gave those two silences a NUMBER
     PER DAY. It did not put them on the live wall, so Shopify's own view
     could say ten people were on the site while this one said nobody, which
     is what sent the owner looking for a fault that was not there.

       UNDECIDED - a passer ping, as before. No id of any kind: a token minted
                   in this tab that every door but /a/h refuses.
       REFUSED    - the SAME passer ping. A no means nothing is kept and
                   nothing is joined, and neither happens here: the ping
                   carries no cookie, no visitor id, nothing to tie it to a
                   person or to another ping, and the server holds it in
                   memory until they leave. It is the live form of the count
                   they are already in.
       GRANTED    - NOT a passer. They are already on the wall through their
                   own events, and a second standing for the same person
                   would be one shopper drawn twice. What they get is a
                   liveness touch carrying the visitor id they consented to,
                   which refreshes the standing they already have and creates
                   nothing. Without it, a reader who is not clicking fades off
                   the wall after four minutes while still on the page - the
                   quiet reader being exactly the person that wall is for. */
  /* The measured lane's keepalive, armed once and left running. Cheap enough
     to call from the event path on every event: it does nothing at all once
     the timer exists. */
  function keepAlive() {
    try {
      if (!granted || !state.aid || hereTimer) return;
      hereTimer = setInterval(hereBeat, HERE_BEAT_MS);
    } catch (err) { /* law 1 */ }
  }

  function here(k, s) {
    try {
      if (granted) {
        if (!state.aid) return;
        /* Any sighting starts the beat, because the handover that follows a
           yes clears the passer's timer and nothing else would ever start
           one again. Only the beat itself is sent: arrivals, leaves and
           everything else about a consented shopper already travel as real
           events, and sending them here too would move them twice. */
        if (!hereTimer) hereTimer = setInterval(hereBeat, HERE_BEAT_MS);
        if (k !== 'here') return;
        hereSend({ v: 1, a: state.aid, k: 'here', p: template() });
        return;
      }
      const body = { v: 1, t: hereToken(), k: k, p: template() };
      const slug = String(s || '').toLowerCase();
      if (slug && HANDLE_RE.test(slug)) body.s = slug;
      hereSend(body);
      if (!hereTimer) hereTimer = setInterval(hereBeat, HERE_BEAT_MS);
    } catch (err) { /* law 1 */ }
  }

  /* The heartbeat: proof of presence for somebody reading without clicking,
     who would otherwise fade off the wall after a minute and a half. Only
     while the page is on screen and has been touched lately. */
  function hereBeat() {
    try {
      /* The beat now serves all three lanes, so it is no longer stopped by an
         answer arriving. It still stops for a tab nobody is looking at. */
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - hereInput > HERE_IDLE_MS) return;
      here('here', pageSlug());
    } catch (err) { /* law 1 */ }
  }

  /* What the wall is told, off the same events the measured lane emits - so
     the two lanes can never disagree about what a page, an add or a search
     is. A confirmed add only, a submitted or landed search only, and never
     the search's words. */
  function sighting(name, o) {
    try {
      const p = (o && o.props) || {};
      switch (name) {
        case 'page.view': here('page', pageSlug()); break;
        case 'product.view': here('product', o && o.subject); break;
        case 'cart.add': if (!p.intent) here('add', o && o.subject); break;
        case 'search.query': if (p.submitted || p.landed) here('search'); break;
        case 'page.leave': here('leave'); break;
        default: break;
      }
    } catch (err) { /* law 1 */ }
  }

  ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(function (type) {
    try { addEventListener(type, function () { hereInput = Date.now(); }, { passive: true, capture: true }); }
    catch (err) { /* law 1 */ }
  });

  /* THE STEP INTO CHECKOUT, for the unmeasured only. The measured lane learns
     it from the checkout's own pixel; a browser that has not said yes is not
     followed there, so the press on the way in is the one moment there is.
     name="checkout" is the hook every route to checkout carries
     (snippets/cart-inner.liquid), the same one juce-capi.js listens for. */
  document.addEventListener('click', function (e) {
    try {
      if (granted) return;
      const t = e.target instanceof Element
        ? e.target.closest('[name="checkout"], [data-loom-cta="cart-checkout"]') : null;
      if (!t) return;
      countAnon('checkout.start', '', null);
      here('checkout');
    } catch (err) { /* law 1 */ }
  }, { capture: true, passive: true });

  function emit(name, o) {
    try {
      o = o || {};
      const now = Date.now();
      /* Activity keeps the session alive; half an hour of silence rotates
         it. Only a hint - the server's gap rule re-derives the truth. */
      if (now - (state.sat || 0) > GAP_MS) state.sid = uuid();
      state.sat = now;
      writeState();

      /* THE FORK. Under a recorded refusal nothing about this event is built
         at all - no id, no timestamp, no url, no props - because building it
         is already more than we are allowed to hold. It is counted and
         forgotten. This sits AFTER writeState(), which is chokepoint 1 and is
         itself a no-op ungranted, so nothing has been stored either. */
      if (declined) { countAnon(name, o.subject, o.props); sighting(name, o); return; }
      /* STILL UNANSWERED: counted and seen as well as buffered. The count
         waits for the way out and a yes drops it (laneNow's header); the
         sighting is the live wall's, and nothing is kept of it. */
      if (!granted) { countAnon(name, o.subject, o.props); sighting(name, o); }
      /* MEASURED, AND THEREFORE INVISIBLE BETWEEN ACTIONS (2026-09-12). The
         sighting above is the live wall's, and it is only ever reached on the
         two ungranted branches - so a consented shopper has never sent the
         wall anything but their stored events, and the idle sweep retires
         them four minutes after the last one. A reader goes quiet long before
         they go away. This starts their keepalive and nothing else: no ping
         is sent from here, the beat does its own sending on its own clock. */
      else keepAlive();

      const ev = { id: uuid(), at: new Date(now).toISOString(), name,
        url: cap(safeHref(), 300), props: leanProps(o.props) };
      if (PAGE_REL) ev.r = PAGE_REL;
      if (o.ref) ev.ref = cap(o.ref, 300);
      /* The session's campaign touch rides every event, so the fold can
         credit a whole session without replaying its landing. */
      if (state.attr && state.attr.last) ev.utm = state.attr.last.utm;
      if (o.subject) ev.subject = cap(o.subject, 100);
      const vid = num(o.variant_id);
      if (vid) ev.variant_id = vid;
      if (o.value_cents != null) {
        const cents = Math.round(Number(o.value_cents));
        if (Number.isFinite(cents)) ev.value_cents = cents;
      }

      q.push(ev);
      if (q.length > CAP) q.splice(0, q.length - CAP);
      mirror();
      /* THE TENTH EVENT USED TO DECIDE HOW THE PAGE SAID GOODBYE. emit()
         flushes as soon as a batch is full, and page.leave is emitted BEFORE
         leave() calls flush(true) - so whenever the leave happened to be the
         tenth event queued, the whole visit went out through fetch keepalive
         from a page that was already unloading, which is precisely the request
         a browser is free to kill. The beacon exists for this moment; the flag
         is how emit() knows the moment has arrived. */
      if (q.length >= FLUSH_AT) flush(leaving);
      else {
        armTimer();
        if (SOON[name] && !soonTimer) {
          soonTimer = setTimeout(function () {
            soonTimer = 0;
            try { if (q.length) flush(false); } catch (err) { /* law 1 */ }
          }, SOON_MS);
        }
      }
    } catch (err) { /* law 1 */ }
  }

  /* A public name must be lowercase [a-z0-9._-], 2 to 48 characters, and is
     taken as-is ONLY when it already lives in x., the open namespace. Every
     other name - dotted or not - is filed under x. + name, because a plug-in
     must never be able to speak a system name: a data-loom="cart.add" would
     forge a CONFIRMED add and "order.place" a purchase. Filing instead of
     rejecting also means a typo'd system-sounding name lands harmlessly in
     x.* rather than in the server's quarantine. */
  function validName(raw) {
    if (typeof raw !== 'string') return null;
    const name = raw.trim().toLowerCase();
    if (!/^[a-z0-9._-]{2,48}$/.test(name)) return null;
    if (name.indexOf('x.') === 0) return name;
    return 'x.' + name;
  }

  /* A guess from the path, not from Liquid: this file ships as one static
     asset and must not need a render pass to know where it stands. */
  const TEMPLATES = [['/products/', 'product'], ['/collections', 'collection'],
    ['/search', 'search'], ['/cart', 'cart'], ['/account', 'account'],
    ['/blogs/', 'article'], ['/pages/', 'page']];
  function template() {
    const p = location.pathname;
    if (p === '/' || p === '') return 'index';
    for (const t of TEMPLATES) if (p.indexOf(t[0]) !== -1) return t[1];
    return 'other';
  }

  /* Once per session, per name. A "start" that fires on every click is not a
     start, and the fold would count one person poking at the finder as
     twenty journeys through it. The marker rides the session id so a genuine
     second visit genuinely counts again. */
  const onced = {};
  function once(key, fn) {
    try {
      const mark = key + ':' + state.sid;
      if (onced[mark]) return;
      onced[mark] = true;
      fn();
    } catch (err) { /* law 1 */ }
  }

  function pathHandle() {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* THE CHECKOUT BRIDGE. Shopify's own first-party visitor cookie, which is
     the very value the checkout Web Pixel is handed as its clientId. The
     pixel runs in a sandbox that can never see our anon id, so this is what
     lets the server recognise a checkout as THIS browser's - one shared
     first-party identifier both sides already have, rather than a guess.
     Absent for a shopper who blocks it, which simply means the checkout
     half of that journey stays its own thread. */
  function shopifyY() {
    const m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
    return m ? cap(decodeURIComponent(m[1]), 64) : undefined;
  }

  /* ---- the context bundle ----
     WHAT THEY ARE SHOPPING ON, sent ONCE per session on the first page view
     and never again: viewport and screen so the shop knows what its pages
     actually get rendered at, timezone and language because they are the
     cheapest honest location signal a browser can give (first-party,
     offline, no lookup, no third party), and the connection class because a
     4G shopper on the N1 is a different customer from one on fibre.

     THIS IS NOT A FINGERPRINT AND MUST NEVER BECOME ONE. Nothing here is
     hashed, nothing is combined into an id, and the server files every one
     of these as an ATTRIBUTE of the device row - never as an identity key,
     never as merge evidence (docs/LOOM.md, migrations/022_profile.sql
     decision 4). Doctrine forbids fingerprinting; describing a browser is a
     different act from recognising one, and the difference is that this is
     never used to decide WHO somebody is.

     Sent once because it does not change mid-session and because the wire
     is meant to stay lean: a bundle on every event would be the fattest
     thing LOOM sends. */
  const CTXKEY = 'juce:loom:ctx';
  /* Which kind of window the page is in: an installed JUCE, or a tab. */
  function displayMode() {
    try {
      if (document.referrer.indexOf('android-app://') === 0) return 'twa';
      const mm = window.matchMedia;
      if (mm) {
        if (mm('(display-mode: fullscreen)').matches) return 'fullscreen';
        if (mm('(display-mode: standalone)').matches) return 'standalone';
        if (mm('(display-mode: minimal-ui)').matches) return 'minimal-ui';
      }
      if (navigator.standalone === true) return 'standalone';
    } catch (err) { /* a browser that cannot say is a browser */ }
    return 'browser';
  }
  function context() {
    try {
      /* Once per session id. The mark rides the same storage the queue
         does, so a private-mode browser simply sends it every page - which
         is correct behaviour, not a bug: it has no session to remember.

         AND AN UNGRANTED VISIT NEVER WRITES THE MARK. This bundle is the
         most consent-sensitive thing the tracker collects, so the marker
         that suppresses it is held to the same rule as every other write:
         nothing on disk before the yes. The cost is that a buffered visit
         may carry the bundle two or three times over; the server upserts
         the device row, which is exactly the case the no-storage branch
         below was already written for. */
      if (!granted) return undefined;
      if (localStorage.getItem(CTXKEY) === state.sid) return undefined;
      localStorage.setItem(CTXKEY, state.sid);
    } catch (err) { /* no storage - send it, the server upserts */ }
    try {
      const n = navigator || {};
      const c = n.connection || {};
      const ctx = {
        vw: innerWidth || undefined,
        vh: innerHeight || undefined,
        sw: (screen && screen.width) || undefined,
        sh: (screen && screen.height) || undefined,
        dpr: devicePixelRatio || undefined,
        tzo: -new Date().getTimezoneOffset(),
        lang: cap(n.language, 16),
        langs: Array.isArray(n.languages) ? n.languages.slice(0, 4) : undefined,
        touch: (n.maxTouchPoints || 0) > 0,
        cores: n.hardwareConcurrency || undefined,
        mem: n.deviceMemory || undefined,
        conn: cap(c.effectiveType, 16),
        save: c.saveData === true ? true : undefined,
        ua: cap(n.userAgent, 300),
        /* WHICH WINDOW THIS IS (2026-09-12). The shop can be installed as an
           app called JUCE, and "did this visit arrive in the app or in a
           browser tab" is the single most useful thing to know about a
           device once it can be either: an installed shopper comes back
           without being fetched, and none of the other columns here can tell
           the two apart. display-mode is the standard answer and covers
           Android, desktop and iOS 26; navigator.standalone is Apple's older
           one and the only answer on iOS before 26; an installed PWA wrapped
           for the Play Store gives itself away in the referrer.

           It is an attribute of the window, not a new way to recognise
           anybody, which is the same line every other field on this bundle
           sits behind. */
        disp: displayMode(),
        /* Whether this browser has notifications on, off, or unanswered.
           The push tables know who is subscribed; this knows how many
           devices could be and are not, which is the denominator that was
           missing. */
        perm: (function () { try { return 'Notification' in window ? Notification.permission : undefined; } catch (err) { return undefined; } })()
      };
      try { ctx.tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (err) { }
      return ctx;
    } catch (err) { return undefined; }
  }

  function pageView(extra) {
    const c = window.theme && window.theme.customer;
    const props = Object.assign({
      template: template(),
      title: cap(document.title, 120),
      /* The account.login heuristic lives here rather than as its own
         event: a page.view of /account with signed_in true IS the signal. */
      signed_in: !!(c && c.signed_in),
      sy: shopifyY(),
      /* Once a session, and undefined every other time - leanProps drops it
         on the round trip, so nothing rides the wire for the other 99%. */
      ctx: context()
    }, extra && extra.props);
    emit('page.view', { ref: extra && extra.ref, props });
  }


  /* ================ WHAT THEY PRESS, AND HOW FAR THEY READ ================
     (owner, 2026-08-26: "a complete map to see also on what buttons a person
     clicked on the most and also able to see button click flow and things
     like that to see how people use CTA's and scroll ... but without breaking
     the system or slowing things down tho".)

     TWO NEW QUESTIONS, ASKED IN THE OPEN NAMESPACE. `x.cta.click` and
     `x.scroll.depth` are `x.*` names rather than two more standing ones, and
     that is the safety property rather than shyness about the vocabulary:
     every number anybody already reads is built from the STANDING list - the
     browse ladder (LOOM_BROWSE_NAMES), the step map the route map is drawn
     from, loom_daily's counters, the funnel - and every one of them maps an
     unrecognised `x.*` name to null and drops it. So this capture goes live
     without a single figure on a single existing panel moving by one.
     steering/14-loom.md calls x.* the escape valve and says a plug-in event
     that proves durable earns promotion into the standing list; the
     promotion, when these have earned it, is a rename in @jennie/shared and
     one in the rollup that reads them.

     AND A REFUSAL STILL COUNTS NOTHING. emit() forks into countAnon() before
     any of this is reached for a browser that said no, and ANON_KINDS names
     five kinds that are not these two. A decliner's presses are counted
     nowhere, not even in aggregate, which is the right answer: the declined
     lane exists to tell the shop what it sells, not how its buttons are used.

     THE COST, MEASURED RATHER THAN PROMISED (the two blocks below carry the
     detail):
       - clicks: NO NEW LISTENER AT ALL. One more branch on the delegated,
         passive, capture-phase click listener that was already there, and the
         only work it does is closest() calls and attribute reads. No
         getBoundingClientRect anywhere - the vertical position comes off
         `pageY`, which the event is already carrying.
       - scroll: one passive listener whose entire body is a comparison, a
         boolean and a requestAnimationFrame. Nothing is measured in the
         scroll frame, nothing is sent per tick, and the listener REMOVES
         ITSELF once the page has been read to the bottom.
     steering/12-performance-playbook.md is the standing law here. */

  const CTA_EVENT = 'x.cta.click';
  const SCROLL_EVENT = 'x.scroll.depth';

  /* THE PAGEVIEW IS THE UNIT OF ORDER, and it needs a name of its own.
     `sid` and `at` already put a session's events in order across pages, but
     "button click flow" is a question about what somebody pressed FIRST on a
     page and what they pressed after it, and two presses can share a
     millisecond. So a click carries the pageview it happened on plus its
     index within that pageview: (at, pv, i) reconstructs the order inside one
     page and (sid, at) carries it across pages, which is both halves of what
     a flow view needs.

     Sixteen hex characters, in memory only, never written to disk - it dies
     with the page exactly as the queue does. The fold counts DISTINCT
     (anon_id, pv), so even a collision between two browsers cannot fuse two
     pageviews into one. */
  let pvid = uuid().replace(/-/g, '').slice(0, 16);
  /* THE CAP, AND IT DROPS WITH A COUNTER RATHER THAN GROWING. A page a script
     is hammering, a rage-clicked button, a feature that re-dispatches its own
     click: any of them could otherwise put a hundred rows in the queue and
     push the real journey off the front of it, because the queue drops
     OLDEST. Forty presses is far more than a person makes on one page; past
     that the count is kept and rides out on page.leave, so a drop is visible
     rather than silent. */
  const CTA_MAX = 40;
  let ctaN = 0;
  let ctaDropped = 0;

  /* ---- naming the control ----
     A STABLE, LOW-CARDINALITY NAME OR NOTHING. The label is the worst
     possible id: it changes with the copy, it differs in every locale, and
     "Add to bag" on 41 product pages is 41 strings the day somebody tests a
     variant. So there are three ways to name a control, they are tried in
     order, and WHICH ONE ANSWERED is recorded in props.how - a guessed name
     and a declared one are different qualities of truth, and a table that
     printed them identically would be lying to whoever read it.

     1. DECLARED. data-loom-cta="cart-checkout" on the control, or on any
        ancestor of it, is the convention, and it is the only one that
        survives a redesign. Put it on a button and that button is named;
        nothing else anywhere has to change.
     2. HOOK. This theme's seams are data attributes, and the ATTRIBUTE NAME
        is exactly the stable, low-cardinality identifier we want:
        data-pair-add="amber-noir" is ONE button called `pair-add` pressed
        with different arguments, not a hundred buttons. The value is
        deliberately never read - the value is where the cardinality lives.
     3. SHAPE. A plain control with no hook at all: its tag plus its id, or
        its tag plus the first class token that looks like a block rather
        than a state. Anything carrying three consecutive digits is refused
        outright, because that is a variant id, a section id or a timestamp
        wearing a class name, and one of those in this column would put a row
        per shopper in the table.

     Nothing else is ever a fallback. A control none of the three can name is
     not recorded at all: a missing button costs one row of a ranking, and a
     cardinality bomb costs the whole table. */
  const CTA_SEL = '[data-loom-cta], a[href], button, summary, [role="button"],'
    + ' input[type="submit"], input[type="button"]';
  const CTA_RE = /^[a-z0-9][a-z0-9._:#-]{0,39}$/;
  const HOOK_RE = /^data-([a-z][a-z0-9-]{1,31})$/;
  /* Hooks whose NAME says nothing because they exist to carry a value, and
     the hooks that belong to this file. Named rather than guessed at: the
     whole reason for reading an attribute name was that it is not free text,
     and a denylist you have to maintain is cheaper than a column you cannot. */
  const HOOK_SKIP = /^(loom|loom-props|loom-cta|loom-surface|handle|id|index|idx|key|val|value|src|href|url|variant|variant-id|product|price|json|state|open|hidden|label|title|text|for|name)$/;
  /* Three digits in a row is an id, never a name. */
  const NOISY = /\d{3}/;
  /* A class that describes what the control is DOING rather than what it is. */
  const STATE_CLASS = /^(is|has|js|u|no)-/;

  /* Returns { id, how, el } - and `el` is the element that CARRIES the name,
     which is not always the one asked about: a press on the words inside a
     product card is named `card` by the card. The heatmap measures and
     indexes against the carrier (anchorOf below); the CTA branch reads only
     the id. */
  function ctaName(el) {
    /* 1. declared */
    try {
      const dec = el.closest('[data-loom-cta]');
      if (dec) {
        const v = String(dec.getAttribute('data-loom-cta') || '').trim().toLowerCase();
        if (CTA_RE.test(v)) return { id: v, how: 'declared', el: dec };
      }
    } catch (err) { /* law 1 */ }
    /* 2. hook. The control's own attributes first, then TWO ancestors and no
       further. Two is not a round number, it is the distance between a
       control and the thing that owns it - `<div data-card><a href>` names
       the link `card`, which is right - while three would start reaching the
       SECTION wrapper, and a rule that names every link inside the finder
       `fx-app` has stopped measuring controls and started measuring pages. */
    try {
      for (let n = el, depth = 0; n && n.nodeType === 1 && depth < 3; n = n.parentElement, depth += 1) {
        const attrs = n.attributes;
        if (!attrs) continue;
        for (let i = 0; i < attrs.length; i += 1) {
          const m = HOOK_RE.exec(attrs[i].name);
          if (!m || HOOK_SKIP.test(m[1]) || NOISY.test(m[1])) continue;
          return { id: m[1], how: 'hook', el: n, val: hookVal(attrs[i].value) };
        }
      }
    } catch (err) { /* law 1 */ }
    /* 3. shape */
    try {
      const tag = String(el.tagName || '').toLowerCase();
      if (!tag) return null;
      const id = String(el.id || '').trim().toLowerCase();
      if (id && !NOISY.test(id) && CTA_RE.test(tag + '#' + id)) {
        return { id: tag + '#' + id, how: 'shape', el: el };
      }
      /* className is a string on an HTMLElement and an SVGAnimatedString on
         an <svg> - which stringifies to "[object SVGAnimatedString]" and
         would have become a class token. classList answers correctly for
         both. */
      const classes = el.classList ? Array.prototype.slice.call(el.classList) : [];
      for (const raw of classes) {
        const c = String(raw).trim().toLowerCase();
        if (!c || NOISY.test(c) || STATE_CLASS.test(c)) continue;
        const shaped = tag + '.' + c;
        if (CTA_RE.test(shaped)) return { id: shaped, how: 'shape', el: el };
      }
      /* A bare control with nothing on it at all. The tag alone is a real
         answer and a useful one - "somebody pressed an unnamed button on the
         cart page" is a bug report - and it cannot explode, because CTA_SEL
         only ever matches seven shapes of control. */
      return { id: tag, how: 'shape', el: el };
    } catch (err) { return null; }
  }

  /* ---- where on the page it happened ----
     THE SECTION, ASKED OF THE DOM RATHER THAN OF LIQUID, for the same reason
     template() is: this file ships as one static asset and must not need a
     render pass to know where it stands. Four answers in order of how far
     they can be trusted, and every one of them shape-gated so that an
     editor-minted id (shopify-section-template--17293__1691234567890) cannot
     turn this column into one row per section instance. */
  const SEC_RE = /^[a-z][a-z0-9_-]{1,31}$/;
  function surfaceOf(el) {
    try {
      /* 1. declared, the same convention as the CTA name. */
      const dec = el.closest('[data-loom-surface]');
      if (dec) {
        const v = String(dec.getAttribute('data-loom-surface') || '').trim().toLowerCase();
        if (SEC_RE.test(v)) return v;
      }
      /* 2. Shopify's own wrapper. A statically rendered section is
         `shopify-section-cart-drawer`; a group or template one is
         `shopify-section-template--123__main`, whose meaning is the tail. */
      const sec = el.closest('.shopify-section, [id^="shopify-section-"]');
      if (sec && sec.id) {
        const raw = String(sec.id).replace(/^shopify-section-/, '');
        const tail = raw.indexOf('__') !== -1 ? raw.slice(raw.lastIndexOf('__') + 2) : raw;
        const v = tail.toLowerCase();
        if (SEC_RE.test(v) && !NOISY.test(v)) return v;
      }
      /* 3. The landmarks that are not sections. A press in the cart drawer
         and a press in the page underneath it are different surfaces even
         when it is the same button, and that is the one distinction the
         wrapper id above cannot make - the drawer is rendered once, at the
         bottom of the layout, whatever page it is opened over. */
      if (el.closest('[data-drawer]')) return 'drawer';
      if (el.closest('dialog, [role="dialog"]')) return 'modal';
      if (el.closest('[data-menu], [data-sheet]')) return 'sheet';
      if (el.closest('[data-dock]')) return 'dock';
      if (el.closest('header, [data-header]')) return 'header';
      if (el.closest('footer')) return 'footer';
    } catch (err) { /* law 1 */ }
    return '';
  }

  /* HOW FAR DOWN THE PAGE THE CONTROL WAS, in tenths, read off the EVENT
     rather than off the element. `pageY` is a document coordinate the browser
     has already computed in order to dispatch the click;
     getBoundingClientRect() would force a layout inside a click handler for a
     number this coarse. Bucketed to tens because "the CTA nobody ever presses
     unless they have scrolled to the bottom" is the question, and a pixel
     offset is a different column entirely.

     A keyboard-activated button dispatches a click with pageY 0, which is not
     a position - so it has none, and the prop is simply absent rather than
     zero. Zero would have read as "at the very top of the page". */
  function posOf(e) {
    try {
      const y = Number(e && e.pageY);
      if (!Number.isFinite(y) || y <= 0) return undefined;
      const h = docHeight();
      if (!(h > 0)) return undefined;
      const p = Math.round((y / h) * 10) * 10;
      return p < 0 ? 0 : p > 100 ? 100 : p;
    } catch (err) { return undefined; }
  }

  function ctaClick(el, e) {
    try {
      if (ctaN >= CTA_MAX) { ctaDropped += 1; return; }
      const named = ctaName(el);
      if (!named || !named.id) return;
      ctaN += 1;
      const props = { pv: pvid, i: ctaN, tpl: template(), how: named.how };
      const sec = surfaceOf(el);
      if (sec) props.sec = sec;
      const pos = posOf(e);
      if (pos !== undefined) props.pos = pos;
      emit(CTA_EVENT, { subject: named.id, props: props });
    } catch (err) { /* law 1 */ }
  }

  /* ---- how far they read ----
     MILESTONES, NOT A STREAM. Four of them - a quarter, a half, three
     quarters, the end - each recorded at most once per pageview, and a jump
     that crosses three at once is ONE event carrying the deepest, because the
     shallower ones are implied by it. The fold takes MAX(depth) per pageview
     and expands, so a pageview that only ever said 100 still counts at 25, 50
     and 75, and a duplicate delivery cannot inflate anything.

     THE SCROLL FRAME DOES NO WORK. The listener is passive and its entire
     body is a comparison, a boolean and a requestAnimationFrame: it never
     reads a layout property, never sends anything, and after the page has
     been read to the bottom it removes itself for good. The measuring happens
     in the rAF, where the one expensive read - scrollHeight, which can force
     a layout - is cached for a second, so scrolling a product page for ten
     seconds costs ten of them on a document that is laying out anyway.
     steering/12-performance-playbook.md forbids driving anything visual off
     scroll; this drives nothing at all, which is the same rule kept harder.

     THE DEPTH IS GA4's SHAPE - the BOTTOM of the viewport as a percentage of
     the page, so the number is arguable against the industry's rather than
     being a private dialect - MEASURED AGAINST THE END OF <main> RATHER THAN
     THE END OF THE DOCUMENT. That one word is the difference between a column
     somebody can act on and a column about the footer.

     (Measured on the live cart page, 2026-09-04: the document is 1973px and
     the footer is 1200px of it, so the content ends at 39%. Against the
     document, a shopper who read that page start to finish files as "did not
     reach halfway", the quarter/half/three-quarters marks all land inside
     boilerplate that is identical on every page of the shop, and the panel
     reports the cart, search and account templates as unread pages when what
     really happened is that there was nothing under them to read. Every
     template pays it; the short ones pay all of it.)

     So the end of the page is the end of `main` - `layout/theme.liquid`
     renders exactly one, on every template, with the footer group and every
     piece of persistent chrome outside it. A page whose CONTENT fits on one
     screen is therefore 100% the moment it is drawn, which is true - there
     was nothing else to see - and it says so with props.short, so nobody is
     ever told a cart page was "read to the end" without also being told why.
     That flag has always been the honest half of this branch and against the
     document it could almost never fire.

     THE HEATMAP'S HEIGHT STAYS THE DOCUMENT'S, and the two are not in
     disagreement: bandStep cuts the whole scrollable page into 32 bands and
     the overlay draws them back over that same page, footer included, so its
     coordinate system has to be the thing being scrolled. This one is a claim
     about how much of the PAGE somebody took in, and the footer is not it. */
  const DEPTHS = [25, 50, 75, 100];
  let hitDepth = 0;
  let scrollQueued = false;
  let scrollOn = false;
  let docH = 0;
  let docHAt = 0;
  let firstMeasure = 0;
  /* WHERE A SCROLL COMES TO REST (2026-10-22, the replay's and the Live
     view's: the quarter marks say how far somebody got, never where they
     stopped to read or that they went back up). One x.scroll.at when the
     page has been still for REST_MS after moving at least half a screen from
     the last one, or onto the very top or bottom: thousandths of the page's
     scroll, nothing else. The settle timer is re-armed from the rAF this
     file already runs for the scroll (never from the scroll event), and
     REST_MAX per page keeps a restless reader to a few dozen rows. */
  const SCROLL_AT = 'x.scroll.at';
  const REST_MS = 700;
  const REST_MAX = 40;
  let restTimer = 0;
  let restN = 0;
  let restY = 0;
  /* The top is where a page opens, not a place anybody scrolled to. */
  let restF = 0;

  function scrollRest() {
    restTimer = 0;
    try {
      if (restN >= REST_MAX || document.visibilityState !== 'visible') return;
      const vh = window.innerHeight || 0;
      const y = window.scrollY || 0;
      const max = docHeight() - vh;
      if (!(vh > 0) || !(max > 40)) return;
      const f = Math.max(0, Math.min(1000, Math.round((y / max) * 1000)));
      const edge = (f === 0 || f === 1000) && f !== restF;
      if (!edge && Math.abs(y - restY) < vh / 2) return;
      restY = y;
      restF = f;
      restN += 1;
      emit(SCROLL_AT, { props: { pv: pvid, f: f } });
    } catch (err) { /* law 1 */ }
  }

  function docHeight() {
    const now = Date.now();
    if (docH > 0 && now - docHAt < 1000) return docH;
    docHAt = now;
    try {
      const d = document.documentElement;
      const b = document.body;
      docH = Math.max(
        d ? d.scrollHeight : 0, b ? b.scrollHeight : 0, d ? d.offsetHeight : 0) || 0;
    } catch (err) { docH = 0; }
    return docH;
  }

  /* WHERE THE PAGE ENDS, as opposed to where the document does. Cached on the
     same one-second dial as docHeight and for the same reason.

     getBoundingClientRect() is a layout read, and in the frame that already
     called docHeight() it is a free one - the layout it needs was computed by
     that call. In a frame that did not (heat capture off), it costs exactly
     what the scrollHeight read it replaces would have.

     THE FALLBACK IS THE DOCUMENT, not zero and not the viewport: a page with
     no `main` is not a page this theme rendered - a Shopify system page, a
     third-party checkout step - and measuring it the old way is the honest
     answer rather than refusing to answer. */
  let readH = 0;
  let readHAt = 0;

  function readHeight() {
    const now = Date.now();
    if (readH > 0 && now - readHAt < 1000) return readH;
    readHAt = now;
    readH = 0;
    try {
      const m = document.getElementById('main') || document.querySelector('main');
      if (m) {
        const bottom = m.getBoundingClientRect().bottom + (window.scrollY || 0);
        if (bottom > 0) readH = Math.round(bottom);
      }
    } catch (err) { readH = 0; }
    if (!(readH > 0)) readH = docHeight();
    return readH;
  }

  function measureScroll() {
    scrollQueued = false;
    if (restN < REST_MAX) {
      clearTimeout(restTimer);
      restTimer = setTimeout(scrollRest, REST_MS);
    }
    /* THE ATTENTION ACCUMULATOR RIDES THIS FRAME rather than a listener of
       its own, which is the whole reason the heatmap costs no new scroll
       work: this rAF was already scheduled, already throttled and already
       reading the two numbers bandStep needs. It runs BEFORE the milestone
       branch below because that branch returns early once the page has been
       read to the bottom, and attention keeps accruing long after that. */
    if (heatOn) bandStep();
    try {
      if (hitDepth >= 100) return;
      const vh = window.innerHeight || 0;
      const h = readHeight();
      if (!(h > 0) || !(vh > 0)) return;
      /* Forty pixels of slack: a page one scrollbar taller than the viewport
         is not a page anybody scrolled. */
      const short = h - vh <= 40;
      const pct = short ? 100 : Math.floor(((window.scrollY + vh) / h) * 100);
      let deepest = 0;
      for (const d of DEPTHS) if (pct >= d) deepest = d;
      if (deepest <= hitDepth) return;
      hitDepth = deepest;
      const props = { pv: pvid, tpl: template(), depth: deepest };
      if (short) props.short = true;
      emit(SCROLL_EVENT, { props: props });
      /* THE MILESTONES ARE DONE BUT THE READING IS NOT. Reaching the bottom
         used to be the end of this listener's life, and it still is when heat
         capture is off; with it on, the listener stays because "how long did
         they spend at the bottom" is a question the milestones never asked
         and the attention map is mostly made of. */
      if (deepest >= 100 && !heatOn && restN >= REST_MAX) scrollStop();
    } catch (err) { /* law 1 */ }
  }

  function onScroll() {
    if (scrollQueued || (hitDepth >= 100 && !heatOn && restN >= REST_MAX)) return;
    scrollQueued = true;
    try { requestAnimationFrame(measureScroll); }
    catch (err) { scrollQueued = false; }
  }

  function scrollStop() {
    if (!scrollOn) return;
    scrollOn = false;
    try { removeEventListener('scroll', onScroll); } catch (err) { /* law 1 */ }
  }

  /* Armed once, and re-armed by a bfcache restore or a soft page() exactly as
     the dwell clock is. THE FIRST MEASUREMENT WAITS, and that wait is the
     whole difference between a useful column and a false one: on arrival the
     document is still growing - images resolving, sections hydrating, the
     sticky bars settling - and a height read at zero files a long product
     page as a short one and stamps it read-to-the-end before anybody has seen
     anything. `load` is the honest signal; the timeout is the backstop for a
     page whose last image never arrives.

     THAT WAIT BELONGS TO THE MILESTONES, NOT TO THE ATTENTION CLOCK. The
     depth events below are a claim about how far somebody got and must not be
     made against a half-built document. The bands are a claim about time, and
     time spent before this fires is still time spent - bandStep credits it
     retroactively against the height it can measure when it is finally
     asked. */
  function scrollStart() {
    if (scrollOn) return;
    scrollOn = true;
    try { addEventListener('scroll', onScroll, { passive: true }); }
    catch (err) { scrollOn = false; }
    clearTimeout(firstMeasure);
    firstMeasure = setTimeout(() => { try { measureScroll(); } catch (err) { /* law 1 */ } }, 2500);
    try {
      if (document.readyState === 'complete') onScroll();
      else addEventListener('load', () => { try { onScroll(); } catch (err) { /* law 1 */ } }, { once: true });
    } catch (err) { /* law 1 */ }
  }

  /* WHERE A SCROLL INSIDE A BOX COMES TO REST (2026-10-27, Nico: the replay
     of a Find your scent visit "doesn't scroll"). The finder, the Layering
     Lab, the menu sheet and the bag hold the page still and scroll a box of
     their own, which the window listener above never hears: a scroll event
     does not bubble, so one listener on the document in the capture phase
     hears every box. The same rule as scrollRest - one x.scroll.at once the
     box has been still for REST_MS after moving a third of itself, or onto
     its very top or bottom - plus the box's anchor as the subject (the
     heat's own, so the replay finds the same box) and `ax: 'x'` for a lane
     scrolled sideways. A box nothing can name is left out rather than read
     as the page. */
  const INNER_MAX = 40;
  let innerN = 0;
  let innerBox = null;
  let innerTimer = 0;
  const innerLast = typeof WeakMap === 'function' ? new WeakMap() : null;

  function innerRest() {
    innerTimer = 0;
    const box = innerBox;
    innerBox = null;
    try {
      if (!box || !innerLast || innerN >= INNER_MAX || document.visibilityState !== 'visible') return;
      if (!box.isConnected) return;
      const maxY = box.scrollHeight - box.clientHeight;
      const maxX = box.scrollWidth - box.clientWidth;
      const was = innerLast.get(box) || { x: 0, y: 0, f: 0, ax: '' };
      const side = maxX > 40 && Math.abs(box.scrollLeft - was.x) > Math.abs(box.scrollTop - was.y);
      const max = side ? maxX : maxY;
      if (!(max > 40)) return;
      const pos = side ? box.scrollLeft : box.scrollTop;
      const f = Math.max(0, Math.min(1000, Math.round((pos / max) * 1000)));
      const span = side ? box.clientWidth : box.clientHeight;
      const edge = (f === 0 || f === 1000) && (f !== was.f || was.ax !== (side ? 'x' : 'y'));
      if (!edge && Math.abs(pos - (side ? was.x : was.y)) < span / 3) return;
      const named = anchorOf(box, surfaceOf(box));
      if (!named.id || named.id.indexOf('/') < 0) return;
      innerLast.set(box, { x: box.scrollLeft, y: box.scrollTop, f: f, ax: side ? 'x' : 'y' });
      innerN += 1;
      const props = { pv: pvid, f: f };
      if (side) props.ax = 'x';
      emit(SCROLL_AT, { subject: cap(named.id, 80), props: props });
    } catch (err) { /* law 1 */ }
  }

  try {
    document.addEventListener('scroll', (e) => {
      try {
        const box = e.target;
        if (!box || box.nodeType !== 1 || box === document.documentElement || box === document.body) return;
        if (innerN >= INNER_MAX) return;
        if (innerBox && innerBox !== box) { clearTimeout(innerTimer); innerRest(); }
        innerBox = box;
        clearTimeout(innerTimer);
        innerTimer = setTimeout(innerRest, REST_MS);
      } catch (err) { /* law 1 */ }
    }, { capture: true, passive: true });
  } catch (err) { /* law 1 */ }

  /* ==================== THE HEATMAP'S HALF OF THE RECORD ====================
     (owner, 2026-09-02: "a heatmap over the website where people scroll,
     click, concentration and stuff in that period ... needs to be quite
     interactive and accurate also".)

     THREE `x.*` NAMES. `x.heat.click` rides the same delegated capture-phase
     click handler the CTA branch already sits on; `x.heat.view` is emitted
     once, at leave, from the accumulator below; `x.heat.swipe` (2026-09-02)
     is the one gesture that needed listeners of its own, and they are
     passive and store two numbers each - see the swipe block.
     migrations/070_heatmap.sql carries the storage argument; this block
     carries the measurement one.

     WHY A CLICK IS NOT STORED AS A PAIR OF PAGE PIXELS. x=980 is the buy
     button on a laptop, the third card of a grid on a tablet, and off the
     side of the document on a phone; and the day a section grows a
     paragraph, every dot below it on every past day is pointing at the wrong
     thing. So a press is stored against an ELEMENT - the nearest ancestor
     that can be named by the same declared > hook > shape ladder ctaName()
     already uses - plus where inside that element's box it landed, in
     percent. The viewer resolves the anchor in the live page and puts the dot
     on the element's real rect, so a section that moved takes its heat with
     it. `py` rides along as the fallback for a press whose anchor has since
     been deleted: a rough dot that says it is rough beats a vanished one.

     THE ONE LAYOUT READ, AND WHY IT IS NOT A REFLOW. getBoundingClientRect()
     on the anchor is unavoidable - the element's box IS the coordinate
     system. It is cheap here specifically because this listener is
     `capture: true`: it runs BEFORE any of the theme's own handlers, so
     nothing has written to the DOM since the browser computed the layout it
     used to hit-test this very click. The read is a cache hit, not a forced
     synchronous reflow. Moving this branch to the bubble phase would quietly
     turn it into one, which is why it must stay where it is.

     THE ATTENTION CLOCK HAS NO TIMER AT ALL, which is the whole reason it is
     affordable. The document is cut into 32 bands; the accumulator remembers
     which bands are on screen and WHEN THAT BECAME TRUE, and credits the
     elapsed time only when the coverage changes - a scroll settling, a resize,
     the tab hiding, the page leaving. Somebody reading one screen for four
     minutes costs exactly two arithmetic passes over 32 slots, not 240
     timer ticks. steering/12-performance-playbook.md is the law here. */

  const HEAT_CLICK = 'x.heat.click';
  const HEAT_VIEW = 'x.heat.view';
  const BANDS = 32;
  /* Presses per pageview. Higher than CTA_MAX because this branch records the
     ones that hit nothing as well, and a frustrated shopper jabbing at a dead
     image is the single most valuable thing on the finished picture - capping
     it at forty would throw away the evidence at exactly the moment it starts
     being interesting. Refusals past the cap are counted and ride out on
     x.heat.view, so a drop is visible rather than silent. */
  const HEAT_MAX = 60;
  /* A press credited to a rage burst: three or more inside this many
     milliseconds and this many pixels of each other. */
  const RAGE_MS = 800;
  const RAGE_PX = 40;
  /* Anything that answers a press: if none of these is an ancestor, the click
     landed on scenery. Deliberately all closest() calls and NOT a
     getComputedStyle cursor test - reading a computed style in a click
     handler is a style recalc for a boolean, and this list answers the same
     question from markup the theme already writes. */
  const ALIVE_SEL = CTA_SEL + ', [data-loom], label, select, textarea, input,'
    + ' [onclick], [tabindex], video, audio, details';

  let heatN = 0;
  let heatDropped = 0;
  let rageAt = 0;
  let rageX = 0;
  let rageY = 0;
  let rageRun = 0;

  const bandMs = new Array(BANDS).fill(0);
  let bandLo = -1;
  let bandHi = -1;
  let bandAt = Date.now();
  let bandShort = false;
  /* WAS ANYBODY LOOKING WHEN THIS PAGEVIEW BEGAN? The stretch before the
     first measurement is credited retroactively (see bandStep), and that is
     only honest for a page somebody was actually watching. A tab opened in
     the background - a middle-click, a "open in new tab" from a search page -
     is not a visit, and crediting its whole lifetime to the top of the
     document would put a page nobody looked at above every page somebody
     read. It starts creditable only if the page starts visible, and becomes
     creditable when it is first looked at (the visibilitychange handler). */
  let bandWatched = true;
  try { bandWatched = !document.hidden; } catch (err) { /* law 1 */ }
  /* Heat capture is on unless this browser is DRIVING a heatmap (heatViewing,
     set by the gate near the top of this file). A staff member walking the
     shop with the overlay open would otherwise write their own inspection
     into the picture they are inspecting - and on a page with forty real
     pageviews that is not a rounding error, it is the brightest spot on the
     map, sitting exactly where the person looking was clicking. The CTA and
     scroll branches are deliberately NOT suspended with it: those numbers
     have been collected since August, a staff visit has always been in them,
     and quietly changing what they mean to serve a new feature is how two
     panels start disagreeing about the same day. */
  let heatOn = !heatViewing;

  const clampBand = (i) => (i < 0 ? 0 : i > BANDS - 1 ? BANDS - 1 : i);

  /* WHICH LAYOUT THIS WAS, and it is a key rather than a filter. A phone and
     a desktop are not two samples of one page - they are two different
     documents, with different sections in a different order - so every row
     the fold writes is keyed on this and the viewer draws exactly one bucket
     at a time. The boundaries are the theme's own: 768 is where the mobile
     sheet gives way, 1280 where the page rail appears. */
  function widthBucket() {
    try {
      return bucketOf(viewportWidth());
    } catch (err) { return 'm'; }
  }

  /* Credit the coverage that just ended, then recompute what is on screen.
     Called only when the answer can have changed.

     THE FIRST MEASUREMENT OF A PAGEVIEW GOES THE OTHER WAY ROUND: it reads
     the position first and credits the elapsed time to what it finds.

     (Measured on production, 2026-09-03: the attention clock used to start at
     the first bandStep, which waits for `load` - a median of 2.0 seconds into
     a pageview on the shop's real traffic, and over 7 seconds for the slowest
     quarter of it. Anything that happened before that was not merely
     uncredited: with every band still zero, heatView() refuses to emit at
     all, so a visitor who left inside that window vanished from the picture
     entirely - no pageview, no reach, and any press they made counted as a
     press against a pageview the denominator never knew about. It fell
     hardest exactly where it is least affordable: the home page is the
     landing page AND the heaviest, and was missing a quarter of its
     pageviews.)

     Reading the position first is honest because the visitor has been where
     they are since the pageview began - unless they scrolled, and a scroll is
     itself one of the things that calls this, so the stretch it closes is the
     one that really was spent at the old position. It also means the height
     used is the one measured NOW rather than at boot, which is what the wait
     was protecting in the first place: the document has finished growing by
     the time anybody asks. */
  function bandStep() {
    try {
      const now = Date.now();
      const dt = now - bandAt;
      bandAt = now;
      /* Ten minutes is the ceiling on one uninterrupted stretch. A longer gap
         than that is a machine that slept or a clock that jumped, not a
         person reading, and crediting it would put a single abandoned tab
         above every real visit on the page. */
      const first = bandLo < 0;
      const credit = dt > 0 && dt <= 600000;
      if (!first && credit) {
        for (let i = bandLo; i <= bandHi; i += 1) bandMs[i] += dt;
      }
      const h = docHeight();
      const vh = window.innerHeight || 0;
      if (!(h > 0) || !(vh > 0)) { bandLo = -1; bandHi = -1; return; }
      /* "IT ALL FITTED ON ONE SCREEN" IS A CLAIM ABOUT A FINISHED DOCUMENT.
         Now that the first measurement can happen before `load` - it happens
         at the leave of a pageview that ended early - a page still fetching
         its images could measure barely taller than the viewport and file
         itself as read-to-the-end. The bands are still credited in that case,
         because time spent is time spent; only this flag waits, and waiting
         understates rather than flatters. */
      bandShort = h - vh <= 40 && document.readyState === 'complete';
      const top = window.scrollY || 0;
      bandLo = clampBand(Math.floor((top / h) * BANDS));
      bandHi = clampBand(Math.floor(((top + vh - 1) / h) * BANDS));
      if (first && credit && bandWatched) {
        for (let i = bandLo; i <= bandHi; i += 1) bandMs[i] += dt;
      }
    } catch (err) { bandLo = -1; bandHi = -1; }
  }

  /* ---- naming the place a press landed ----
     FOUR SEGMENTS, ALWAYS, so the viewer can split on '/' without a parser:
     `surface/how/name/index`, with '-' standing in for an answer there is
     none of. The surface is surfaceOf()'s, unchanged. The name is ctaName()'s
     ladder applied to the nearest nameable ancestor - which for a press on
     the words inside a product card resolves to the card, and that is the
     right unit: "they press the middle of the card, never the button" is a
     finding, and it needs the card's box to be visible as a box.

     THE INDEX IS WHICH ONE OF THEM IT WAS, counted inside the surface, and it
     is what makes the anchor resolvable rather than merely descriptive: the
     viewer runs the same query and takes the same element. Bounded at 99
     because a hundredth match is a list, and heat on the hundredth row of a
     list is a question nobody is asking.

     A DOM QUERY IN A CLICK HANDLER is affordable at most HEAT_MAX times per
     pageview and is scoped to the surface rather than the document. It reads;
     it never writes; and by the capture-phase argument above the layout it
     reads is already clean. */
  const MAX_INDEX = 99;

  /* Returns { id, box }: the anchor string, and the element its fractions are
     measured against - which is THE ELEMENT THAT CARRIES THE NAME, not the
     one under the finger. A press on the words inside a product card is
     named `card` by the card two ancestors up, so the index is which card
     that was and the fraction is where in the CARD's box it landed; the
     viewer resolves the same card and applies the same fraction to the same
     box. Measuring against the target instead (the first version did) put
     the press at the words' fraction of the card's box, which is a different
     place, and counted the index against a list the target was never in -
     so every row in every pane of the menu sheet resolved to pane 0. When
     nothing inside the surface could be named, the surface itself is the
     box. */
  function anchorOf(el, sec) {
    const bare = { id: sec || '', box: sec ? (surfaceRootOf(sec, el) || surfaceRoot(sec)) : null };
    try {
      const named = ctaName(el);
      if (!named || !named.id) return bare;
      const carrier = named.el || el;
      const how = named.how === 'declared' ? 'd' : named.how === 'hook' ? 'h' : 's';
      /* The heat anchor carries the hook's value (hookVal above); the CTA
         subject does not. Forty is the door's cap on the name segment. */
      let name = named.id;
      if (how === 'h' && named.val && (name + ':' + named.val).length <= 40) {
        name = name + ':' + named.val;
      }
      /* The root the index is counted from: THE SURFACE ROOT THAT CONTAINS THE
         PRESS, not the first element of that surface's kind on the page. The
         menu sheet and the cart drawer are both `modal`; counting a sheet
         press against the drawer found nothing, and the viewer resolves the
         same containing root by trying every candidate. Without a surface the
         document is the root, which is correct and simply less stable. */
      let root = document;
      if (sec) {
        const found = surfaceRootOf(sec, carrier) || surfaceRoot(sec);
        if (found) root = found;
      }
      let idx = -1;
      try {
        const all = root.querySelectorAll(heatSelector(how, name));
        if (all.length > MAX_INDEX + 1) return bare;
        for (let i = 0; i < all.length; i += 1) {
          if (all[i] === carrier) { idx = i; break; }
        }
      } catch (err) { /* an unqueryable shape name - fall back to the surface */
        return bare;
      }
      /* A carrier the query cannot find again (a shape whose selector does
         not match itself) is an anchor the viewer could never resolve. */
      if (idx < 0) return bare;
      return { id: (sec || '-') + '/' + how + '/' + name + '/' + idx, box: carrier };
    } catch (err) { return bare; }
  }

  /* Percent of the box, rounded to the step the table is keyed on. Two
     percent of a 120px button is under three pixels - finer than the blur the
     viewer draws with - and it caps an anchor at 51x51 cells instead of the
     10201 a whole-percent key would allow. */
  const step2 = (v) => {
    const p = Math.round((v * 50)) * 2;
    return p < 0 ? 0 : p > 100 ? 100 : p;
  };

  function heatClick(t, e) {
    try {
      if (!heatOn) return;
      if (heatN >= HEAT_MAX) { heatDropped += 1; return; }
      /* A keyboard-activated control dispatches a click with no real
         coordinates. It is a genuine press and the CTA branch counts it, but
         it has no PLACE, and a heatmap is only about place. */
      const cx = Number(e && e.clientX);
      const cy = Number(e && e.clientY);
      if (!Number.isFinite(cx) || !Number.isFinite(cy) || (cx === 0 && cy === 0)) return;
      heatN += 1;

      const sec = surfaceOf(t);
      const named = anchorOf(t, sec);
      const anchor = named.id;
      const props = { pv: pvid, tpl: template(), w: widthBucket() };

      /* Where in the anchor's box, when there is a box to be in - AND WHEN THE
         PRESS WAS IN IT. A tap on a modal dialog's backdrop is dispatched to
         the <dialog> itself, so its anchor is the sheet and its coordinates
         are outside the sheet's rect; clamping those into the box filed every
         tap-outside-to-close as a press on the sheet's top-left corner. Such
         a press keeps its anchor and its depth and simply has no cell. */
      const box = named.box;
      if (box && box.getBoundingClientRect) {
        const r = box.getBoundingClientRect();
        if (r.width > 0 && r.height > 0
            && cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom) {
          props.fx = step2((cx - r.left) / r.width);
          props.fy = step2((cy - r.top) / r.height);
        }
      }
      /* THE FALLBACK, and it is written for every press rather than only for
         the anchorless ones: it is what lets a press survive the deletion of
         the section it happened in. pageY is already on the event. */
      const h = docHeight();
      const py = h > 0 ? Math.round(((cy + (window.scrollY || 0)) / h) * 100) : -1;
      if (py >= 0) props.py = py > 100 ? 100 : py;

      /* Did it hit anything? */
      let alive = false;
      try { alive = !!t.closest(ALIVE_SEL); } catch (err) { alive = true; }
      if (!alive) props.dead = true;

      /* A burst in one small place. The run is kept rather than the flag so
         the THIRD press is what turns it on - two presses is a double-click,
         which is a habit, not a complaint. */
      const now = Date.now();
      if (now - rageAt < RAGE_MS
          && Math.abs(cx - rageX) < RAGE_PX && Math.abs(cy - rageY) < RAGE_PX) {
        rageRun += 1;
      } else {
        rageRun = 1;
      }
      rageAt = now; rageX = cx; rageY = cy;
      if (rageRun >= 3) props.rage = true;

      emit(HEAT_CLICK, { subject: anchor ? cap(anchor, 80) : undefined, props: props });
    } catch (err) { /* law 1 */ }
  }

  /* ==================== SWIPES AND DRAGS ====================
     (owner, 2026-09-02: "also when someone swipes and drags and things like
     that" - of a heatmap that only knew about presses.)

     A press is a point; a swipe is a point and a direction, and on a phone it
     is how a shopper moves through a lane, a gallery or a sheet. The click
     branch is blind to every one of them: a lane swiped two hundred times a
     day and never tapped reads as furniture nobody touches. This records the
     gesture as `x.heat.swipe`, anchored to the thing that was swiped - the
     scroll container when the finger was in one, otherwise the nearest
     nameable ancestor of what was under it - with the direction the finger
     moved, how far, and where in the box it started.

     WHAT IS NOT A SWIPE. A vertical gesture whose scroller is the page itself
     is scrolling, and the scroll layer already has it; recording it here too
     would paint every product page as one enormous downward arrow. So a
     vertical gesture counts only INSIDE an inner scroller (the sheet, the
     drawer, a filter rail), and a horizontal one counts anywhere, because on
     a phone there is nothing else a horizontal gesture can mean. With a
     mouse the bar is higher still - a drag that selects a sentence is not a
     swipe - so a mouse drag counts only inside a scroller or on a control
     built to be dragged.

     TOUCH IS READ OFF THE TOUCH EVENTS, NOT THE POINTER EVENTS, on purpose:
     the moment a touch turns into a native scroll the browser fires
     pointercancel and stops delivering pointer events, but touchmove and
     touchend keep coming. Pointer events serve the mouse and the pen. Every
     listener is passive and every one of them stores two numbers; nothing is
     measured until the finger lifts, and then once. */
  const SWIPE_EVENT = 'x.heat.swipe';
  const SWIPE_MAX = 40;
  /* Under this it is a tap with a tremor in it; over this many milliseconds
     it is a finger resting, not moving. */
  const SWIPE_PX = 24;
  const SWIPE_MS = 4000;
  /* Things a MOUSE may drag and have it count outside a scroller. */
  const DRAG_SEL = 'input[type="range"], [draggable="true"], .shot-wrap, [data-menu-grip],'
    + ' [data-gallery], [data-stage], [data-swipe]';
  let swipeN = 0;
  let gest = null;

  /* The nearest ancestor that scrolls on this axis, or null when the page
     itself is the scroller. The cheap size test runs first, so the computed
     style is read only on a box that actually overflows. */
  function scrollerOf(el, horizontal) {
    try {
      for (let n = el, i = 0; n && n.nodeType === 1 && i < 14; n = n.parentElement, i += 1) {
        if (n === document.body || n === document.documentElement) return null;
        const over = horizontal ? n.scrollWidth > n.clientWidth + 1 : n.scrollHeight > n.clientHeight + 1;
        if (!over) continue;
        const o = getComputedStyle(n)[horizontal ? 'overflowX' : 'overflowY'];
        if (o === 'auto' || o === 'scroll') return n;
      }
    } catch (err) { /* law 1 */ }
    return null;
  }

  function gestureStart(x, y, t, mouse) {
    if (!heatOn || !(t instanceof Element)) { gest = null; return; }
    if (t.closest('[data-loom-skip]')) { gest = null; return; }
    gest = { x: x, y: y, at: Date.now(), t: t, mouse: mouse };
  }

  function gestureEnd(x, y) {
    try {
      const g = gest;
      gest = null;
      if (!g || !heatOn) return;
      if (swipeN >= SWIPE_MAX) return;
      const dx = x - g.x;
      const dy = y - g.y;
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      const dist = Math.max(ax, ay);
      const ms = Date.now() - g.at;
      if (!(dist >= SWIPE_PX) || ms > SWIPE_MS) return;
      const horizontal = ax >= ay;
      const scroller = scrollerOf(g.t, horizontal);
      if (!horizontal && !scroller) return;
      if (g.mouse && !scroller && !g.t.closest(DRAG_SEL)) return;
      swipeN += 1;

      /* The thing that was swiped is the scroller when there was one - the
         lane, not the card inside it that happened to be under the thumb. */
      const el = scroller || g.t;
      const sec = surfaceOf(el);
      const named = anchorOf(el, sec);
      const anchor = named.id;
      const props = {
        pv: pvid, tpl: template(), w: widthBucket(),
        dir: horizontal ? (dx < 0 ? 'l' : 'r') : (dy < 0 ? 'u' : 'd'),
        px: Math.min(4000, Math.round(dist)),
        ms: Math.min(10000, Math.round(ms))
      };
      const box = named.box;
      if (box && box.getBoundingClientRect) {
        const r = box.getBoundingClientRect();
        if (r.width > 0 && r.height > 0
            && g.x >= r.left && g.x <= r.right && g.y >= r.top && g.y <= r.bottom) {
          props.fx = step2((g.x - r.left) / r.width);
          props.fy = step2((g.y - r.top) / r.height);
        }
      }
      const h = docHeight();
      const py = h > 0 ? Math.round(((g.y + (window.scrollY || 0)) / h) * 100) : -1;
      if (py >= 0) props.py = py > 100 ? 100 : py;
      emit(SWIPE_EVENT, { subject: anchor ? cap(anchor, 80) : undefined, props: props });
    } catch (err) { gest = null; }
  }

  document.addEventListener('touchstart', (e) => {
    try {
      const tt = e.touches && e.touches[0];
      /* A second finger is a pinch, and a pinch is not a swipe. */
      if (!tt || e.touches.length !== 1) { gest = null; return; }
      gestureStart(tt.clientX, tt.clientY, e.target, false);
    } catch (err) { gest = null; }
  }, { capture: true, passive: true });
  document.addEventListener('touchend', (e) => {
    try {
      const tt = e.changedTouches && e.changedTouches[0];
      if (!tt) { gest = null; return; }
      gestureEnd(tt.clientX, tt.clientY);
    } catch (err) { gest = null; }
  }, { capture: true, passive: true });
  document.addEventListener('touchcancel', () => { gest = null; }, { capture: true, passive: true });
  document.addEventListener('pointerdown', (e) => {
    try {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      gestureStart(e.clientX, e.clientY, e.target, true);
    } catch (err) { gest = null; }
  }, { capture: true, passive: true });
  /* On the window, because a drag that ends outside the document - past the
     edge of a lane and off the page - still ends. */
  addEventListener('pointerup', (e) => {
    try {
      if (e.pointerType === 'touch') return;
      gestureEnd(e.clientX, e.clientY);
    } catch (err) { gest = null; }
  }, { capture: true, passive: true });
  addEventListener('pointercancel', (e) => {
    if (e.pointerType !== 'touch') gest = null;
  }, { capture: true, passive: true });

  /* ONE ROW PER PAGEVIEW, AT LEAVE, carrying the whole attention profile as a
     single array of DECISECONDS. Tenths rather than milliseconds because the
     JSON has to fit inside sanitizeProps' 1200-character cap with room to
     spare, and nobody has ever asked a heatmap a question a tenth of a second
     could not answer.

     REACH AND ATTENTION COME OUT OF THE SAME ARRAY - reach is "this band is
     above zero", attention is the value itself - which is the reason they are
     one event rather than two. Fed by two events, a dropped batch could leave
     a page whose attention map covered ground its scroll map said nobody
     reached, and no reader could tell which half to believe.

     THE ARRAY IS CUMULATIVE AND THE FOLD TAKES MAX PER PAGEVIEW. THIS IS A
     CONTRACT, not an implementation detail - core/loom-fold.ts foldHeatDay()
     depends on it and breaking it here silently doubles every number there.
     A shopper who tabs away and comes back leaves TWICE, and each leave emits
     this event carrying everything accumulated so far, because the
     alternative - emitting once and going quiet - throws away all the reading
     they did after their first interruption. Cumulative values can only be
     folded by MAX, and MAX has the second virtue of making a duplicate
     delivery free: the same row twice is the same number, not twice it. Only
     newPageview() ever zeroes bandMs. */
  function heatView() {
    try {
      if (!heatOn) return;
      bandStep();
      const b = new Array(BANDS);
      let total = 0;
      let any = false;
      for (let i = 0; i < BANDS; i += 1) {
        /* Capped at ten minutes per band, the same ceiling bandStep applies
           to a single stretch, so one forgotten tab cannot outweigh a
           thousand real reads of the same page. */
        const ds = Math.min(Math.round(bandMs[i] / 100), 6000);
        b[i] = ds;
        total += ds;
        if (ds > 0) any = true;
      }
      if (!any) return; /* a page nobody was on is not a pageview to draw */
      const props = {
        pv: pvid, tpl: template(), w: widthBucket(), b: b,
        title: cap(document.title, 120)
      };
      if (bandShort) props.short = true;
      if (heatN) props.clicks = heatN;
      if (heatDropped) props.dropped = heatDropped;
      emit(HEAT_VIEW, { props: props });
    } catch (err) { /* law 1 */ }
  }

  /* A NEW PAGEVIEW IS A NEW UNIT OF ORDER AND A NEW READ. Called for a
     bfcache restore and for window.loom.page(), which are the two ways this
     page becomes a different page without the file booting again. */
  function newPageview() {
    try {
      pvid = uuid().replace(/-/g, '').slice(0, 16);
      ctaN = 0;
      ctaDropped = 0;
      hitDepth = 0;
      docH = 0;
      docHAt = 0;
      clearTimeout(restTimer);
      restTimer = 0;
      restN = 0;
      innerN = 0;
      clearTimeout(innerTimer);
      innerBox = null;
      restY = window.scrollY || 0;
      restF = restY > 0 ? -1 : 0;
      /* The heat accumulator belongs to the pageview exactly as the press
         counter does: a bfcache restore is a fresh read of the page and its
         attention starts from nothing. */
      heatN = 0;
      heatDropped = 0;
      rageRun = 0;
      swipeN = 0;
      gest = null;
      for (let i = 0; i < BANDS; i += 1) bandMs[i] = 0;
      bandLo = -1;
      bandHi = -1;
      bandAt = Date.now();
      try { bandWatched = !document.hidden; } catch (err) { bandWatched = true; }
      scrollStop();
      scrollStart();
    } catch (err) { /* law 1 */ }
  }

  /* ---- the page's own lifecycle ----
     One leave per disappearance: pagehide and visibilitychange-hidden race
     on real navigations, so the flag makes whichever lands first the only
     one that counts, and a return to the tab re-arms it - a shopper who
     tabs away twice genuinely left twice. Leaving is also the last chance
     to flush, which is what the beacon exists for. */
  let viewStart = Date.now();
  let left = false;

  /* ---- THE PAGE'S OWN VITALS (2026-09-22, lane 8) ----
     steering/theme/12 §8 budgets LCP, INP and CLS at p75 "field", and the
     theme had no field numbers at all: every figure in the playbook came
     from a lab run. This is the field half, with no library: the same five
     observers web-vitals uses, read the way it reads them.

       lcp   the last largest paint before the first input (the browser stops
             reporting it after one), from the moment the page became the
             shopper's (activationStart, so a prerendered page is not
             credited with the time it spent waiting unseen)
       inp   the worst interaction, or the 98th percentile once there are
             fifty (one in fifty is forgiven, as the metric defines it)
       cls   the worst session window: shifts less than a second apart, a
             window no longer than five seconds, input-caused shifts excluded
       fcp, ttfb   for reading the first two against

     ONE EVENT A PAGEVIEW, `x.vitals`, riding the goodbye beacon beside
     page.leave (so it costs no request of its own), with the template, how
     the page was reached and the connection the browser reports. The
     envelope's `rel` is the version, which is what lets a release be read
     against the one before it. Only a lane that already sends events sends
     this (never under a recorded refusal), and a bfcache restore sends none:
     its numbers would be the original load's. */
  const cwv = { lcp: 0, cls: 0, fcp: 0, ttfb: 0, inp: -1 };
  let cwvSent = false;
  try {
    if ('PerformanceObserver' in window && performance.getEntriesByType) {
      const nav = performance.getEntriesByType('navigation')[0];
      const act = (nav && nav.activationStart) || 0;
      const at = (t) => Math.max(0, Math.round(t - act));
      if (nav && nav.responseStart) cwv.ttfb = at(nav.responseStart);
      const watch = (type, fn, extra) => {
        try {
          new PerformanceObserver((l) => l.getEntries().forEach(fn))
            .observe(Object.assign({ type: type, buffered: true }, extra || {}));
        } catch (err) { /* an older browser simply reports less */ }
      };
      watch('paint', (e) => { if (e.name === 'first-contentful-paint') cwv.fcp = at(e.startTime); });
      watch('largest-contentful-paint', (e) => { cwv.lcp = at(e.startTime); });
      let sv = 0, sFirst = 0, sLast = 0;
      watch('layout-shift', (e) => {
        if (e.hadRecentInput) return;
        if (sv && e.startTime - sLast < 1000 && e.startTime - sFirst < 5000) sv += e.value;
        else { sv = e.value; sFirst = e.startTime; }
        sLast = e.startTime;
        if (sv > cwv.cls) cwv.cls = sv;
      });
      /* the ten worst interactions and how many there were, which is all a
         98th percentile of a page's interactions ever needs */
      const worst = [];
      const seen = new Set();
      const took = (id, d) => {
        if (!id) return;
        const hit = worst.find((w) => w.id === id);
        if (hit) { if (d > hit.d) hit.d = d; }
        else { seen.add(id); worst.push({ id: id, d: d }); }
        worst.sort((a, b) => b.d - a.d);
        if (worst.length > 10) worst.length = 10;
        cwv.inp = Math.round(worst[Math.min(worst.length - 1, Math.floor(seen.size / 50))].d);
      };
      watch('event', (e) => took(e.interactionId, e.duration), { durationThreshold: 40 });
      watch('first-input', (e) => took(e.interactionId || -1, e.duration));
      /* an interaction faster than the 40ms floor still counts as one */
      addEventListener('pointerup', () => { if (cwv.inp < 0) cwv.inp = 0; }, { passive: true, capture: true });
      addEventListener('keyup', () => { if (cwv.inp < 0) cwv.inp = 0; }, { passive: true, capture: true });
    }
  } catch (err) { /* law 1 */ }
  function sendVitals() {
    if (cwvSent || declined || !(cwv.fcp || cwv.lcp)) return;
    cwvSent = true;
    const nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
    const c = navigator.connection || {};
    const p = {
      lcp: cwv.lcp, cls: Math.round(cwv.cls * 1000) / 1000, fcp: cwv.fcp, ttfb: cwv.ttfb,
      tpl: template(), nav: nav.activationStart > 0 ? 'prerender' : (nav.type || 'navigate'),
      vw: window.innerWidth
    };
    if (cwv.inp >= 0) p.inp = cwv.inp;
    if (c.effectiveType) p.ect = c.effectiveType;
    if (c.saveData) p.save = true;
    if (navigator.deviceMemory) p.mem = navigator.deviceMemory;
    emit('x.vitals', { props: p });
  }

  function leave() {
    if (left) return;
    left = true;
    /* Raised BEFORE the emit, because that emit may itself be the tenth event
       and trigger its own flush - which has to be a beacon, not a fetch. */
    leaving = true;
    /* THE LAST WORD ON THIS PAGEVIEW, and three of these four numbers are new
       (2026-08-26). `depth` is the deepest milestone reached, carried again
       here so a pageview whose x.scroll.depth events were lost to a dropped
       batch still says how far it got; `ctas` is how many controls were
       pressed; `cta_dropped` is how many presses were REFUSED by the per-
       pageview cap, because a drop that nothing records is the one failure a
       recorder must never have. */
    const gone = { dwell_ms: Date.now() - viewStart };
    if (hitDepth) gone.depth = hitDepth;
    if (ctaN) gone.ctas = ctaN;
    if (ctaDropped) gone.cta_dropped = ctaDropped;
    emit('page.leave', { props: gone });
    try { sendVitals(); } catch (err) { /* law 1 */ }
    /* AFTER page.leave AND BEFORE the flush: the attention profile is the
       other half of this pageview's goodbye and has to be in the same beacon.
       Emitted second so that if the batch is one event over the wire's cap,
       the one that gets split off is the heatmap's row and not the journey's
       - a lost heat row costs one page one sample, a lost page.leave costs a
       session its ending. */
    heatView();
    flush(true);
    /* The declined lane's own goodbye. flush() is closed under a refusal and
       must stay closed; this is the counter emptying itself, which is a
       different door and a different body. */
    flushAnon(true);
    leaving = false;
  }

  document.addEventListener('visibilitychange', () => {
    try {
      if (document.visibilityState === 'hidden') leave();
      else {
        /* They came back: a later hide is a new leave, and its dwell clock
           starts NOW - the hidden stretch was not time with the page, and
           counting from the original load would inflate every return. */
        left = false;
        viewStart = Date.now();
        /* The attention clock is restarted the same way and for the same
           reason. leave() already credited everything up to the hide through
           heatView()'s bandStep; without this line the whole hidden stretch
           would be credited to whatever band happened to be on screen when
           they tabbed away, and an overnight tab would out-weigh every real
           reader of the page. */
        bandAt = Date.now();
        /* And a tab that OPENED in the background becomes a real visit at
           this moment, which is the first one anybody looked at it. */
        bandWatched = true;
        /* The presence lane said `leave` on the way out; say where they are
           again, so the wall brings them back rather than letting them fade. */
        hereInput = Date.now();
        if (!granted && hereTok) here('page', pageSlug());
      }
    } catch (err) { /* law 1 */ }
  });
  addEventListener('pagehide', () => { try { leave(); } catch (err) { } });

  /* A bfcache restore is a re-appearance the boot path never sees - pageshow
     with persisted set is the only signal there is. Counted as a page.view
     flagged bfcache, and the dwell clock starts over because theirs did. */
  addEventListener('pageshow', (e) => {
    try {
      if (!e.persisted) return;
      viewStart = Date.now();
      left = false;
      /* the restored page's vitals would be the original load's (THE
         PAGE'S OWN VITALS): it sends none */
      cwvSent = true;
      /* A restored page is a new pageview to the flow and a new page to
         read: its clicks belong to their own sequence and its scroll clock
         starts again, exactly as the dwell clock does above. */
      newPageview();
      pageView({ props: { bfcache: true } });
    } catch (err) { /* law 1 */ }
  });

  /* ---- the delegated listeners ----
     No early return between checks, deliberately: one tap can honestly be
     two things (a product link inside a card that also carries data-loom),
     and each check answers for itself. */
  document.addEventListener('click', (e) => {
    try {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      /* Anything that says it is not the shop. The heatmap viewer's own
         hosts carry this (assets/juce-heatmap.js) so a press on its panel is
         never filed as a press on the page it is standing over. */
      if (t.closest('[data-loom-skip]')) return;

      /* The plug-in seam: name from the attribute, props from its JSON
         sibling. Bad JSON is the author's problem, never the shopper's -
         the event still fires, just without the props. */
      const plug = t.closest('[data-loom]');
      if (plug) {
        const name = validName(plug.getAttribute('data-loom'));
        if (name) {
          let props;
          try { props = JSON.parse(plug.getAttribute('data-loom-props')); }
          catch (err) { props = undefined; }
          emit(name, { props });
        }
      }

      if (t.closest('[data-chat-open], [data-magic]')) emit('chat.open', {});
      if (t.closest('[data-drawer-open]')) emit('cart.view', {});
      if (t.closest('[data-menu-open]')) emit('menu.open', {});
      if (t.closest('[data-search-open]')) emit('search.open', {});

      /* ---- THE JUCE JOURNEYS ----
         The scent finder, the discovery builder and the layering duet are
         the features this shop is actually about, and until now not one of
         them reached the record: every name below was reserved in
         LOOM_EVENTS, wired into the Sankey's step map and into the fold, and
         never emitted by anything. A flow view that cannot show the finder
         is a flow view of somebody else's shop.

         Each one hangs off the outermost stable hook the section already
         renders, so no feature code changes and none of it knows this
         exists. `once` keeps a start honest: a person poking at the finder
         for five minutes started it once. */
      /* [data-fx-go-say], not [data-fx-go]: the 2026-08-21 rebuild renamed
         the sentence door's submit and this selector was never moved, so
         that arm was dead. [data-fx-answer] never survived the rebuild at
         all. The nebula's family and note discs (2026-08-26) are starts
         too - opening a family IS using the finder. */
      if (t.closest('[data-fx-go-say], [data-fx-pick], [data-fx-step], [data-neb-fam], [data-neb-note]')) {
        once('finder.start', () => emit('finder.start', {}));
      }
      /* Reaching the answer is the completion, and it is a real navigation
         inside the section rather than a page change, so nothing else would
         ever have caught it.

         [data-fx-read] IS NOT THE ANSWER. It is the "Reading: warm, evening"
         paragraph that echoes the brief back while somebody is still building
         it - mid-consultation, before a single question is settled - so a tap
         landing on it filed a completed consultation and the funnel counted a
         finish that had not happened. The seams that actually call reveal()
         are the done button, a try chip and the ask form's own submit (bound
         below); those three are the completion.

         And once per session, like the start. Without the guard, somebody who
         pressed "Show me the answer", read it, changed an answer and pressed
         it again completed the finder twice - the same distortion `once` was
         added to the start to prevent. */
      if (t.closest('[data-fx-done], [data-fx-try]')) {
        once('finder.complete', () => emit('finder.complete', {}));
      }

      /* ---- WHAT THEY ACTUALLY ANSWERED ----
         (owner, 2026-09-02: "we want to know what exactly they doing in the
         scent finder".)

         finder.start and finder.complete are a funnel. They say a
         consultation happened and say nothing whatever about what was IN it,
         which is the part worth watching: somebody narrowing to feminine,
         warm, evening has told the shop more in four taps than any survey
         would get out of them.

         One event per settled answer, as facet:value - deliberately the same
         shape `filter.apply` uses for the collection rail, because they are
         the same kind of statement made in two rooms and a reader should not
         have to learn two vocabularies.

         In the x. namespace and emitted from HERE rather than from the
         finder's own controller, which is the rule this whole file keeps:
         instrumentation is delegated and lives with the tracker, so page code
         can never speak a canonical name (window.loom.track force-prefixes x.
         for exactly that reason).

         A DEAD OPTION IS NOT AN ANSWER. theme.js refuses a tap on .is-dead -
         nothing in the shop satisfies it alongside what is already picked -
         so recording one would file a brief the shopper never gave. The clear
         option is the opposite: it IS an answer, and the answer is "anything". */
      /* ON OR OFF. Every option in the finder toggles - a second tap on
         Feminine takes it back - and this listener runs in the capture
         phase, BEFORE theme.js flips the state, so the option's current
         `is-on` says what the tap is about to do. Without it a shopper who
         picked and unpicked was counted as picking twice, and "what people
         select" was a count of hesitation. The settled brief travels on
         x.finder.step (theme.js, loomBrief); this is the gesture. */
      var fxp = t.closest('[data-fx-pick]');
      if (fxp && !fxp.classList.contains('is-dead')) {
        var facet = cap(fxp.getAttribute('data-fx-pick'), 24);
        var value = cap(fxp.getAttribute('data-fx-value'), 32);
        var wasOn = fxp.classList.contains('is-on');
        if (facet) {
          emit('x.finder.pick', {
            subject: facet + ':' + (value || 'anything'),
            props: wasOn ? { off: true } : {}
          });
        }
      }
      var fxt = t.closest('[data-fx-toggle]');
      if (fxt) emit('x.finder.pick', { subject: cap(fxt.getAttribute('data-fx-toggle'), 24) + ':yes' });
      /* A skipped question is a fact about the brief too, and the one a
         reader would otherwise mistake for a question never reached. */
      var fxs = t.closest('[data-fx-skip]');
      if (fxs) emit('x.finder.pick', { subject: cap(fxs.getAttribute('data-fx-skip'), 24) + ':skipped' });

      /* ---- THE FINDER'S OTHER DOORS (2026-09-03) ----
         What somebody did AROUND the questions: went back (and from where),
         left the room, opened the notes detour, switched the mark to
         Rule out, took the answer into the layering room, or handed it to
         Jennie. One name, subject as facet:value like the picks, so the
         analytics page reads them off one column. The layering door and
         the Jennie door are the two that matter most - they are what the
         answer was FOR. `data-at` on the section is the panel the room is
         showing, kept current by theme.js go(). */
      var fxRoom = t.closest('[data-finder]');
      if (fxRoom) {
        var fxAt = cap(fxRoom.getAttribute('data-at'), 16) || '';
        var fxLayer = t.closest('[data-fx-layer]');
        var fxTap = fxLayer ? 'layer:' + cap(fxLayer.getAttribute('data-fx-layer'), 64)
          : t.closest('[data-fx-hand], [data-fx-jennie]') ? 'jennie'
          : t.closest('[data-fx-back], [data-fx-edgeback]') ? 'back:' + fxAt
          : t.closest('[data-fx-exit]') ? 'exit:' + fxAt
          : t.closest('[data-fx-step="notes"]') ? 'notes'
          : t.closest('[data-fx-try]') ? 'try'
          : t.closest('[data-fx-mark]') ? 'mark:' + (cap(t.closest('[data-fx-mark]').getAttribute('data-fx-mark'), 8) || '')
          /* the close sits INSIDE the opened family's disc, so it is asked
             before the family rung or a close reads as opening it again */
          : t.closest('[data-neb-close]') ? 'close'
          : t.closest('[data-neb-fam]') ? 'family:' + (cap(t.closest('[data-neb-fam]').getAttribute('data-neb-fam'), 32) || '')
          : '';
        if (fxTap) emit('x.finder.tap', { subject: fxTap });
      }

      const dpick = t.closest('[data-disco-pick]');
      if (dpick || t.closest('[data-disco-pref], [data-disco-surprise]')) {
        once('disco.start', () => emit('disco.start', {}));
      }
      if (dpick) {
        emit('x.disco-pick', { props: { handle: cap(dpick.getAttribute('data-disco-pick'), 100) } });
      }
      /* The tray's add is the whole point of the builder: a discovery set
         that reaches the cart is the shop's single most important funnel,
         and it is the step a sample-to-bottle question is asked against. */
      if (t.closest('[data-disco-add]')) emit('disco.complete', {});

      const dt = t.closest('[data-dt-pick]');
      if (dt) {
        emit('duet.view', { subject: cap(dt.getAttribute('data-dt-pick'), 100) });
      }

      /* ---- WHAT THE DUET'S QUIZ WAS ANSWERED WITH (2026-09-03) ----
         The create-a-pair quiz builds its options in JS, every one
         `data-dt-qpick="<question>"` with the value in data-dt-qval - the
         finder's shape one room along, so it gets the finder's record: one
         event per tap, facet:value, `off` when the tap takes an answer back
         (capture phase, so `is-on` is still the state before the tap), and
         a dead option is not an answer. The settled brief rides
         x.duet.step (theme.js). */
      var dq = t.closest('[data-dt-qpick]');
      if (dq && !dq.classList.contains('is-dead')) {
        var dqKey = cap(dq.getAttribute('data-dt-qpick'), 24);
        var dqVal = cap(dq.getAttribute('data-dt-qval'), 32);
        var dqOn = dq.classList.contains('is-on');
        if (dqKey) {
          emit('x.duet.pick', {
            subject: dqKey + ':' + (dqVal || 'anything'),
            props: dqOn ? { off: true } : {}
          });
        }
      }
      /* ---- THE DUET'S OTHER DOORS ----
         The cover door (I have one / starting fresh), the vibe, an
         alternate swapped onto the stage, the way out of the quiz into the
         deck, the deck's family chips, Jennie, Back, the exit. The reveal
         itself - which pair, by which route - is announced by theme.js as
         x.duet.reveal because only the controller knows the partner. */
      var dtRoom = t.closest('[data-duet]');
      if (dtRoom) {
        var dtAt = cap(dtRoom.getAttribute('data-at'), 16) || '';
        var dtMode = t.closest('[data-dt-mode]');
        var dtVibe = t.closest('[data-dt-vibe]');
        var dtAlt = t.closest('[data-dt-alt]');
        var dtFam = t.closest('[data-dt-fam]');
        var dtTap = dtMode ? 'door:' + (cap(dtMode.getAttribute('data-dt-mode'), 8) || '')
          : dtVibe ? 'vibe:' + (cap(dtVibe.getAttribute('data-dt-vibe'), 16) || '')
          : dtAlt ? 'alt:' + (cap(dtAlt.getAttribute('data-dt-alt'), 64) || '')
          : t.closest('[data-dt-qdeck]') ? 'deck'
          : t.closest('[data-dt-qrun]') ? 'run'
          : t.closest('[data-dt-hand]') ? 'jennie'
          : t.closest('[data-dt-again]') ? 'again'
          : t.closest('[data-dt-revibe]') ? 'revibe'
          : t.closest('[data-dt-back], [data-dt-edgeback]') ? 'back:' + dtAt
          : t.closest('[data-dt-exit]') ? 'exit:' + dtAt
          : dtFam ? 'filter:' + (cap(dtFam.getAttribute('data-dt-fam'), 24) || 'all')
          : '';
        if (dtTap) emit('x.duet.tap', { subject: dtTap });
      }
      /* [data-dt-add] carries the PARTNER's handle, which is the thing that
         gets bought - and it is the only hook here that means "add". An
         earlier pass listened on [data-dt-slot], a container div, and on
         [data-dt-rise], which is an ANIMATION hook sitting on the section's
         paragraphs and doors: duet.add fired on almost any click in the
         duet and the number meant nothing. */
      const dtAdd = t.closest('[data-dt-add]');
      if (dtAdd) {
        emit('duet.add', { subject: cap(dtAdd.getAttribute('data-dt-add'), 100) });
      }

      /* The PDP's layering panel. A click on a partner or on the reasoning
         is somebody thinking about a second bottle, which is the highest
         intent signal on a product page that is not an add. */
      const lay = t.closest('[data-layer-acts] a, [data-layer-alts] a, [data-layer-why]');
      if (lay) {
        once('layer.view', () => emit('layer.view', { subject: pathHandle() || undefined }));
      }

      /* THE LAYERING ADD, AND IT IS THE SAME BUTTON EVERYWHERE. theme.js
         renders [data-pair-add] into the PDP's layering panel, into the cart
         drawer's "Layers with your bag", and into the duet - one hook, one
         listener, and the SURFACE is read off whichever container it landed
         in. This is the signal that says a layering suggestion actually
         earned a sale rather than merely being shown.

         The handle on the attribute is the PARTNER being added, not the page
         somebody is standing on, which is exactly what attribution needs:
         the thing that ends up in the order. */
      const pairAdd = t.closest('[data-pair-add]');
      if (pairAdd) {
        var surface = 'pair';
        if (t.closest('[data-drawer-pairs]')) surface = 'cart-pairs';
        else if (t.closest('[data-layer-acts], [data-layer-alts]')) surface = 'pdp-layer';
        else if (t.closest('[data-dt-deck], [data-dt-panel], [data-duet]')) surface = 'duet';
        /* the finder's own answer sells too, and until this rung existed its
           every sale fell through to the 'pair' fallback - "layering,
           surface not recorded" - which buried the one number that says the
           finder earns its keep. The peek dialog is appended inside the
           section, so its adds land on the right surface through the same
           ancestor walk. */
        else if (t.closest('[data-finder]')) surface = 'finder';
        const props = {
          surface: surface,
          size: cap(pairAdd.getAttribute('data-pair-size'), 20)
        };
        /* ---- WHAT AN ADD-BOTH ACTUALLY SOLD ----
           The PDP band offers the PAIR whenever the scent the page is about is
           missing from the bag, and one press then buys two bottles. The
           partner stays the subject, as on every other pair button anywhere;
           `both` names the anchor. Without it the anchor's line would reach
           credit with no surface attached to it and be filed under "found it
           themselves" - a band that sold two scents recorded as having sold
           one, and the honest half of the number lost. */
        const both = pairAdd.getAttribute('data-pair-both');
        if (both) props.both = cap(both, 100);
        emit('x.pair-add', {
          subject: cap(pairAdd.getAttribute('data-pair-add'), 100),
          props: props
        });
      }

      /* The finder's quieter yes - "Try it in 10ml" - adds through its own
         handler and carried no hook this file reads, so the small-bottle
         half of the finder's sales simply vanished. Same event, same
         surface, size told honestly. */
      const fxMini = t.closest('[data-fx-mini]');
      if (fxMini) {
        emit('x.pair-add', {
          subject: cap(fxMini.getAttribute('data-fx-mini'), 100),
          props: { surface: 'finder', size: '10ml' }
        });
      }

      /* Every control on the collection rail, and it does not matter which:
         the settled state is read off the address bar 800ms from now, so one
         branch answers for a facet chip, a price band, an active pill's x,
         Clear all, and anything added to the rail after this was written. */
      if (t.closest('[data-facet][data-val], [data-cf-band], [data-remove], '
        + '[data-cfilter-clear], [data-cf-search-clear]')) refineSoon();

      /* A tap on a search RESULT. It is also a product link, so product.click
         fires too - deliberately: one tap can honestly be two things, which
         is why there is no early return anywhere in this handler. */
      if (t.closest('[data-search-panel]') && t.closest('a[href]')) {
        const sr = t.closest('a[href]');
        emit('search.click', {
          subject: lastQuery || undefined,
          props: { href: cap(sr.getAttribute('href'), 200) }
        });
      }

      /* ---- THE CTA ----
         Last in the handler and deliberately so: everything above answers a
         question about a NAMED feature of the shop, and this answers the
         general one - "which control was that". A single tap can honestly be
         a product click AND a CTA press, which is why there is no early
         return anywhere in this handler and why there is none here either. */
      const control = t.closest(CTA_SEL);
      if (control) ctaClick(control, e);

      /* ---- THE HEATMAP ----
         Asked of the TARGET rather than of the control, which is the one
         difference between this branch and the one above it and the entire
         reason both exist. The CTA branch answers "which button", so it
         starts by finding a button and says nothing when there is not one -
         and a press that hit no button is precisely what a heatmap is for.
         This branch takes the element actually under the finger, names the
         nearest thing it can, and records the miss as a miss. */
      heatClick(t, e);

      const a = t.closest('a[href*="/products/"]');
      if (a) {
        const href = a.getAttribute('href') || '';
        const m = href.match(/\/products\/([^/?#]+)/);
        const vm = href.match(/[?&]variant=(\d+)/);
        const card = a.closest('[data-card]');
        if (m) emit('product.click', {
          subject: m[1],
          variant_id: vm ? vm[1] : undefined,
          props: card
            ? { card: true, handle: card.getAttribute('data-handle') || undefined }
            : {}
        });
      }
    } catch (err) { /* law 1 */ }
  }, { capture: true, passive: true });

  /* An add SUBMITTED is intent; an add CONFIRMED is the fetch wrap below
     seeing the cart answer ok. Both are named cart.add, and the intent one
     carries props.intent so the fold counts confirmations rather than
     button presses - theme.js intercepts most submits and fetches itself,
     so one shopper add usually produces both events. */
  document.addEventListener('submit', (e) => {
    try {
      const form = e.target instanceof Element ? e.target.closest('[data-product-form]') : null;
      if (!form) return;
      const picked = form.querySelector('input[name="id"]:checked')
        || form.querySelector('[data-variant-input]');
      emit('cart.add', {
        subject: pathHandle() || undefined,
        variant_id: picked ? picked.value : undefined,
        props: { intent: true }
      });
    } catch (err) { /* law 1 */ }
  }, true);

  /* ---- the third door an email comes through ----
     Checkout welds on the cart token and chat welds on the widget id, so a
     shopper who buys or who talks is joined to the browsing that led there.
     A shopper who only SUBSCRIBES was not: they type an address into the
     footer, Shopify makes a customer out of it, the customers/create webhook
     files that person - and the weeks of browsing that persuaded them to
     type it stayed anonymous forever, because nothing tied the two together.

     What travels is a SHA-256 of the address, never the address. The event
     stream is scrubbed of personal data on purpose and a newsletter signup is
     not a reason to start putting it back; a hash is enough for the server to
     recognise the same person when Shopify tells it about them, and useless
     to anybody reading the table. identify() carries the real address for the
     claim path, which the server already treats as stated evidence only.

     Fired before the form navigates, so it is flushed rather than queued. */
  document.addEventListener('submit', (e) => {
    try {
      const form = e.target instanceof Element ? e.target : null;
      if (!form || !form.matches('form')) return;
      const field = form.querySelector('input[name="contact[email]"], input[name="customer[email]"]');
      if (!field || !field.value) return;
      const email = field.value.trim().toLowerCase();
      if (email.length > 200 || !/^[^@\s]{1,64}@[^@\s.]+(\.[^@\s.]+)+$/.test(email)) return;

      /* Stated, for the claim that rides the next envelope. */
      window.loom.identify({ email: email });

      /* A SIGN-IN IS NOT A SIGN-UP (2026-09-22, lane 8, tracking audit). The
         login form carries customer[email] too, so every sign-in attempt was
         filed as subscribe.email with where: 'account', and so was a password
         reset and a message sent through the contact form. Shopify names each
         form in its hidden form_type; only a newsletter (customer) and a new
         account (create_customer) are a sign-up. The others still state the
         email above, which is all the weld needs from them. */
      const ftEl = form.querySelector('input[name="form_type"]');
      const ft = ftEl ? String(ftEl.value || '') : '';
      const act = form.getAttribute('action') || '';
      if (ft === 'customer_login' || ft === 'recover_customer_password' || ft === 'contact'
        || /^\/account\/(login|recover)/.test(act)) return;
      const where = ft === 'create_customer' || act.indexOf('/account') === 0
        ? 'account' : 'newsletter';
      /* crypto.subtle exists in every secure context; the shop is https. If
         it is somehow missing, the event still goes with no hash and the
         weld simply does not happen - law 1, never break the page. */
      var subtle = window.crypto && window.crypto.subtle;
      if (!subtle || typeof TextEncoder === 'undefined') {
        emit('subscribe.email', { props: { where: where } });
        flush(true);
        return;
      }
      subtle.digest('SHA-256', new TextEncoder().encode(email)).then(function (buf) {
        try {
          var hex = Array.prototype.map.call(new Uint8Array(buf), function (b) {
            return ('0' + b.toString(16)).slice(-2);
          }).join('');
          emit('subscribe.email', { props: { sha: hex, where: where } });
          /* The page is on its way out - a beacon, not a timer. */
          flush(true);
        } catch (err) { /* law 1 */ }
      }).catch(function () { /* no hash, no weld, no harm */ });
    } catch (err) { /* law 1 */ }
  }, true);

  /* ---- what they are looking for ----
     THE HIGHEST-VALUE QUESTION A SMALL SHOP CAN ASK ITS OWN SEARCH BOX is
     "what are people searching for that we do not sell", and it was
     unanswerable: search.query is in the vocabulary, the fold builds
     loom_sessions.searched[] from it, the digest ships that array to the
     model - and nothing ever emitted one, so the column was structurally
     always empty and the AI was handed an empty list as a fact.

     Debounced, and only on a settled query: a keystroke is not a search, and
     one event per letter of "amber" would be five rows saying nothing. Also
     deduped against the last one sent, because a pop-up search that is
     closed and reopened re-fires input with the same text. */
  let lastQuery = '';
  let queryTimer = 0;
  document.addEventListener('input', (e) => {
    try {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      /* Before the search matcher, not after: the rail's own search field
         matches BOTH - a shopper typing "vanilla" into the rail is searching
         and refining at once, and the record should say so twice.

         The two range hooks are for the twin-thumb price slider, which
         initCollectionFilter still drives but which main-collection.liquid
         does not currently render (the price facet is band chips, and those
         come through the click branch above). Listed anyway because the
         theme half is live code: the day the thumbs come back, the record
         should not need a second deploy to notice them. */
      if (t.matches('[data-cf-search], [data-cf-range-lo], [data-cf-range-hi]')) refineSoon();
      if (!t.matches('[data-search-input], input[type="search"][name="q"]')) return;
      const value = t.value.trim();
      clearTimeout(queryTimer);
      if (value.length < 2 || value === lastQuery) return;
      queryTimer = setTimeout(() => {
        try {
          if (t.value.trim() !== value) return;   /* they kept typing */
          lastQuery = value;
          emit('search.query', {
            subject: value,
            props: { len: value.length, live: !!t.closest('[data-search-panel], [data-search]') }
          });
        } catch (err) { /* law 1 */ }
      }, 900);
    } catch (err) { /* law 1 */ }
  }, true);

  /* THE THIRD WAY INTO THE ANSWER, and the only one that is not a click.
     Typing a brief and pressing enter runs reveal() through the ask form's own
     submit handler, so a completion reached that way was invisible to the
     click hook above - which meant the shoppers who used the finder the way
     the section is written to be used were the ones who never appeared as
     having finished it. Same `once` key, so whichever route they took the
     session completes exactly once.

     Capture, like every other hook here: theme.js calls preventDefault on this
     form and fetches its own answer, and a cancelled event still reaches a
     capture-phase listener. */
  document.addEventListener('submit', (e) => {
    try {
      /* .sf-say is the rebuilt section's own class (2026-08-21); the old
         .fx-ask__form no longer exists anywhere, so this listener was dead
         and the typed-brief completion path went unmeasured. */
      const form = e.target instanceof Element ? e.target.closest('.sf-say, .fx-ask__form') : null;
      if (!form) return;
      once('finder.complete', () => emit('finder.complete', {}));
    } catch (err) { /* law 1 */ }
  }, true);

  /* ---- A VIEW THE DOM CANNOT ANNOUNCE ----
     The duet's create-a-new-pair quiz (2026-08-26) reaches the reveal with
     no deck tap - the engine chooses the anchor - so the click hook above
     never sees a [data-dt-pick]. The controller announces the fact as a
     DOM event instead, and THIS file decides whether it becomes a record:
     the name must sit in a hard allowlist, because the whole reason
     window.loom.track() force-prefixes x. is that page code must not be
     able to speak canonical names. An announced fact is data; the
     allowlist is the boundary. */
  /* finder.complete joined 2026-09-03: the presence question is skipped by
     the ROOM whenever it could not change the answer (D13 in theme.js), so a
     walk finished through Continue on the third question reached the answer
     with no [data-fx-done] press for the click hook to see. theme.js
     announces the arrival with the scent as subject; `once` holds here
     exactly as it does on the click path, so both doors together still
     complete a session exactly once. */
  /* app.install and app.open joined 2026-09-12: the shop can be installed as
     an app now, and assets/juce-install.js announces both moments through
     this same door rather than growing a tracker API of its own. app.install
     is the once-ever moment a device became the app; app.open is once a
     session from inside it, which is what makes "how much of the week's
     trade came through the app" a question with an answer. */
  const VIEW_BRIDGE = { 'duet.view': 1, 'finder.complete': 1, 'app.install': 1, 'app.open': 1 };
  document.addEventListener('juce:loomview', (e) => {
    try {
      const d = (e && e.detail) || {};
      const name = String(d.name || '');
      if (!VIEW_BRIDGE[name]) return;
      const o = {
        subject: d.subject ? cap(String(d.subject), 100) : undefined,
        props: d.props && typeof d.props === 'object' ? d.props : undefined
      };
      if (name === 'finder.complete') { once('finder.complete', () => emit(name, o)); return; }
      emit(name, o);
    } catch (err) { /* law 1 */ }
  });

  /* A submitted search is a settled one whatever the debounce thought. */
  document.addEventListener('submit', (e) => {
    try {
      const form = e.target instanceof Element ? e.target : null;
      if (!form || !form.matches('form[action*="/search"]')) return;
      const input = form.querySelector('input[name="q"]');
      const value = input && input.value ? input.value.trim() : '';
      if (!value || value === lastQuery) return;
      lastQuery = value;
      emit('search.query', { subject: value, props: { len: value.length, submitted: true } });
      /* the /search page this submit opens must not count it again (below) */
      try { sessionStorage.setItem('juce:loom:sq', JSON.stringify({ q: value, t: Date.now() })); } catch (err) { /* private mode */ }
    } catch (err) { /* law 1 */ }
  }, true);

  /* A landing on /search?q= is a search too - the progressive path, and the
     one every crawler-shy shopper who pressed enter takes. */
  try {
    if (location.pathname.indexOf('/search') === 0) {
      const term = new URLSearchParams(location.search).get('q');
      if (term && term.trim()) {
        lastQuery = term.trim();
        /* ONE SEARCH, ONE COUNT (2026-09-22, lane 8, tracking audit). A search
           submitted from the pop-up was emitted as submitted on the way out
           and again as landed here, and the folds and the anonymous count
           both take either - so every search pressed with enter counted
           twice. The submit leaves its query for this tab; a landing on that
           same query within a minute is the same search. A live (debounced)
           search is not written there: the folds do not count it, so its
           landing is still the one that counts. */
        let dup = false;
        try {
          const sq = JSON.parse(sessionStorage.getItem('juce:loom:sq') || 'null');
          sessionStorage.removeItem('juce:loom:sq');
          dup = !!(sq && sq.q === lastQuery && Date.now() - sq.t < 60000);
        } catch (err) { dup = false; }
        if (!dup) emit('search.query', { subject: lastQuery, props: { landed: true } });
      }
    }
  } catch (err) { /* law 1 */ }

  /* ---- what they NARROWED it to ----
     A REFINEMENT IS A SENTENCE THE SHOPPER TYPED WITH THEIR THUMBS, and it
     was the one thing this shop's best-instrumented page never said out
     loud. Somebody who narrows the collection to fresh + summer + under R500
     has told us more about what they want than any answer to any question we
     would dare to ask them - and every byte of it lived in a query string
     that safeHref() strips two lines before the event is built, for the very
     good reason that a Shopify URL can carry a credential.

     So the facts travel as PROPS on a name of their own, read off a closed
     list of keys. Nothing that is not a facet can ride: an ?activation_token
     is not in the list, so it cannot end up in the record by accident.

     WHY THE ADDRESS BAR AND NOT THE CHIPS. initCollectionFilter writes its
     settled state to the URL on every apply (syncUrl), and that one place
     answers for every control at once - the facet chips, the price thumbs,
     the sort select, the rail search, and a shared link somebody simply
     arrived on. Reading the chips instead would mean knowing which of five
     controls moved, and would silently miss the sixth one somebody adds
     later. This file's whole discipline is to watch stable seams rather than
     feature code, and the URL is the most stable seam the rail has.

     One settled event per refinement, not one per tap: the debounce is
     longer than search's because a person setting four facets is having one
     thought, and the dedupe means the same shelf is never described twice. */

  /* ---- which families to look for, asked of the rail rather than restated ----
     THE LIST USED TO LIVE HERE TWICE. initCollectionFilter (theme.js) has a
     CF_FACETS array that is both the shelf's own state and the set of keys
     syncUrl() writes, and this file carried a hand-copied echo of it. The two
     agreed and nothing made them: adding a sixth family to the rail would
     have left this reading five, and the symptom of that is not an error, it
     is a facet that quietly never reaches loom_sessions.refined, the journey
     tool, or the 360's WHAT THEY LOOK FOR. Silence is the worst failure a
     recorder has, because everything downstream still looks healthy.

     THE VALUES STILL COME FROM THE URL - every word above this still holds.
     Only the list of NAMES to look up moved, and it moved to the one place
     that cannot disagree with the rail: the rail's own markup. Every family
     is a <details data-cf-group="preference"> whose chips carry
     data-facet="preference", both server-rendered by main-collection.liquid,
     and that string is the same string state[] is keyed by and syncUrl() puts
     in the query. A family cannot exist without declaring itself - the rail's
     click handler reads data-facet to know what a tap toggled - so a sixth
     facet is now recorded by default instead of dropped by default.

     WHY NOT A SHARED CONSTANT. theme.js and this file are separate module
     graphs (layout/theme.liquid gives each its own <script type=module>, on
     versioned asset_url paths), so importing CF_FACETS from there would fetch
     and execute a second copy of a 530KB side-effectful module. The only
     other spelling is a global that theme.js publishes, which is precisely
     the feature-code coupling this file exists to avoid.

     AND WHY NOT WHITELIST THE QUERY STRING BY SHAPE. Accepting any key that
     merely LOOKS like a facet would record a new one by default too, but it
     would invert the guarantee worth keeping: a Shopify URL can carry a
     credential, and the denylist of credential names that approach needs is a
     list you find out is incomplete afterwards. Asking the DOM keeps the
     closed list closed - ?activation_token is not excluded by being
     remembered, it is excluded by never being asked for. */

  /* Declared like a family but not one: price is a range and rides pmin/pmax
     below. Anything else that is neither simply has no key in the URL, so an
     unrecognised group costs one lookup that misses and nothing more. */
  const CF_NOT_FACET = { price: 1 };
  /* Old names the rail's own hydrate() still resolves, so a link shared
     before a rename records as the facet it means. Closed by definition: a
     family added tomorrow has no legacy spelling to carry. */
  const CF_LEGACY = { preference: 'type', occasion: 'time' };
  /* Last resort. If the markup is ever restructured out from under the query
     below, recording falls back to exactly what it does today rather than to
     nothing - and the DOM wins whenever it answers at all. */
  const CF_FALLBACK = ['preference', 'season', 'occasion', 'family', 'notes'];

  function cfFacets(root) {
    const out = [];
    try {
      for (const n of root.querySelectorAll('[data-cf-group], [data-facet]')) {
        const f = n.getAttribute('data-cf-group') || n.getAttribute('data-facet');
        /* The shape a state key has. Anything else is not a name syncUrl()
           could have written, so it is not a name worth asking the URL for. */
        if (!f || CF_NOT_FACET[f] || !/^[a-z][a-z0-9-]{1,23}$/.test(f)) continue;
        if (out.indexOf(f) < 0 && out.length < 24) out.push(f);
      }
    } catch (err) { /* law 1 */ }
    return out.length ? out : CF_FALLBACK;
  }

  /* The shelf as it stands, or null if this page has no rail on it. */
  function refineState() {
    const root = document.querySelector('[data-cfilter]');
    if (!root) return null;
    const on = [];
    let q = '', sort = '';
    try {
      const p = new URLSearchParams(location.search);
      for (const f of cfFacets(root)) {
        const legacy = CF_LEGACY[f];
        const raw = p.get(f) || (legacy ? p.get(legacy) : null);
        if (!raw) continue;
        for (const v of String(raw).split(',')) {
          const val = v.trim().toLowerCase();
          /* Same shape the rail's own keys have. Anything else is somebody
             hand-editing the URL, and it does not belong in the record. */
          if (val && /^[a-z0-9-]{1,40}$/.test(val) && on.length < 12) {
            on.push(f + ':' + val);
          }
        }
      }
      /* ---- the three that are one key each ----
         price, q and sort cannot drift the way the families could, because
         there is no list to fall out of step with: syncUrl() writes exactly
         these three names and this reads exactly these three names. What CAN
         drift is the SHAPE, and it fails the same silent way - a value the
         gate does not recognise is dropped without a sound - so each gate
         below is only as tight as the privacy question actually needs.

         Rands, exactly as the rail puts them there (syncUrl divides cents by
         100 and rounds). The decimal tail is not written today and is taken
         anyway, so the day a half-rand bound appears the band still records
         instead of vanishing. An open end stays open rather than being filled
         in with a bound the shopper never chose. */
      const money = /^\d{1,6}(\.\d{1,2})?$/;
      const lo = p.get('pmin'), hi = p.get('pmax');
      if ((lo || hi) && on.length < 12) {
        const band = (lo && money.test(lo) ? lo : '') + '-'
          + (hi && money.test(hi) ? hi : '');
        if (band !== '-') on.push('price:' + band);
      }
      /* The RAIL's search box, never Shopify's: refineState only runs where
         [data-cfilter] is, and that is the collection template alone. Free
         text somebody typed, so there is no shape to check and the cap is the
         whole guard. */
      const sq = p.get('q');
      if (sq) q = cap(sq.trim(), 60);
      /* The <select>'s own vocabulary, which today is featured / price-asc /
         price-desc / az and tomorrow is whatever main-collection.liquid gains.
         Digits and underscores pass because the next option added is as
         likely to be best_selling or newest2 as another hyphenated pair, and
         a sort missing from the record is the exact silence this pass exists
         to remove. Still a shape gate: an opaque token is not a sort value. */
      const so = p.get('sort');
      if (so && /^[a-z][a-z0-9_-]{0,23}$/.test(so)) sort = so;
    } catch (err) { return null; }

    /* What the shelf answered. Read off the same two nodes the shopper is
       looking at, so the number in the record is the number on the screen. */
    let n = null, of = null;
    try {
      const cn = document.querySelector('[data-coll-count]');
      if (cn) n = num(cn.textContent.replace(/[^0-9]/g, '')) || 0;
      const ofn = document.querySelector('[data-cf-of]');
      if (ofn) of = num(ofn.getAttribute('data-cf-of'));
    } catch (err) { /* counts are decoration; the facets are the point */ }

    return { on: on, q: q, sort: sort, n: n, of: of };
  }

  let lastRefine = null;
  let refineTimer = 0;
  function refineSoon(landed) {
    try {
      clearTimeout(refineTimer);
      refineTimer = setTimeout(() => {
        try {
          const st = refineState();
          if (!st) return;
          const sig = st.on.join('|') + '#' + st.q + '#' + st.sort;
          /* An empty shelf is only news when it USED to be full: that is a
             shopper clearing everything, which is a real move. An untouched
             collection page is not a refinement and never emits. */
          if (!st.on.length && !st.q && !lastRefine) return;
          if (sig === lastRefine) return;
          lastRefine = sig;
          const props = { on: st.on };
          if (st.q) props.q = st.q;
          if (st.sort) props.sort = st.sort;
          if (st.n != null) props.n = st.n;
          if (st.of != null) props.of = st.of;
          if (landed) props.landed = true;
          emit('filter.apply', {
            subject: location.pathname.split('/collections/')[1] || 'all',
            props: props
          });
        } catch (err) { /* law 1 */ }
      /* A landing waits LONGER, not less: the counts are read off the page,
         and on arrival the rail has not hydrated yet - the number in the bar
         is still Liquid's unfiltered one. Reading it at zero would file a
         refinement to seven scents as a refinement to forty-one. */
      }, landed ? 1200 : 800);
    } catch (err) { /* law 1 */ }
  }

  /* Arriving on a refinement is a refinement. It is how our own carry links
     work ("keep my refinement" from a vibe shelf to the whole catalogue) and
     how a shopper shares one, so a landing that already carries facets is
     the shopper's choice as much as a tap is. */
  refineSoon(true);

  /* Size switches. isTrusted separates a real tap from the programmatic
     mirrors the theme runs (the dock echoing the PDP picker and back), so
     the fold can count decisions instead of reflections. */
  document.addEventListener('change', (e) => {
    try {
      const t = e.target;
      /* The sort control is a <select>, so it has to be asked about before
         the HTMLInputElement gate below turns it away. */
      if (t instanceof Element && t.matches('[data-cf-sort]')) refineSoon();
      if (!(t instanceof HTMLInputElement)) return;
      if (t.name !== 'id' && !t.closest('[data-line-size]')) return;
      const card = t.closest('[data-card]');
      emit('variant.switch', {
        subject: (card && card.getAttribute('data-handle')) || pathHandle() || undefined,
        variant_id: t.value,
        props: { trusted: e.isTrusted === true }
      });
    } catch (err) { /* law 1 */ }
  }, true);

  /* The chat seam's own event: everything that sends a prompt through the
     door dispatches juce:chat (see initJennie in theme.js), so one listener
     hears every entry point ever added. */
  document.addEventListener('juce:chat', (e) => {
    try {
      const src = e.detail && e.detail.source;
      emit('chat.prompt', { props: src ? { source: cap(src, 40) } : {} });
    } catch (err) { /* law 1 */ }
  });

  /* ---- cart truth: the fetch wrap ----
     Wrapped once, after theme.js has booted (script order guarantees it).
     The rules: never block, never reorder, and on ANY error inside our
     inspection the original response sails through untouched - the clone
     is what makes reading the body safe, the page's own reader still gets
     an unread stream. change/update answer with the whole cart; add
     answers with the line(s) added, so each field is read where it exists. */
  /* AN UPDATE THAT TOUCHES NO LINE IS NOT A CART CHANGE (2026-09-22, lane 8,
     tracking audit). /cart/update.js also carries attribute-only writes - the
     maintenance pass parks its key there, the tester swap writes a note - and
     each was filed as a cart.update beside the real edit. A body that names
     no line (no updates, id, line or quantity) is skipped; a body this cannot
     read is counted as before. */
  function touchesLines(init) {
    try {
      const b = init && init.body;
      if (!b) return true;
      if (typeof b === 'string') {
        const t = b.trim();
        if (t.charAt(0) === '{') {
          const o = JSON.parse(t);
          return !!(o && (o.updates || o.id != null || o.line != null || o.quantity != null || o.items));
        }
        return /(^|&)(updates|id|line|quantity)(\[|%5B|=)/i.test(t);
      }
      if ((typeof FormData !== 'undefined' && b instanceof FormData)
        || (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams)) {
        for (const k of b.keys()) if (/^(updates|id|line|quantity)(\[|$)/.test(k)) return true;
        return false;
      }
    } catch (err) { /* unreadable: count it */ }
    return true;
  }
  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const out = origFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input
        : (input && input.url) || String(input || '');
      let name = url.indexOf('/cart/add.js') !== -1 ? 'cart.add'
        : (url.indexOf('/cart/change.js') !== -1 || url.indexOf('/cart/update.js') !== -1)
          ? 'cart.update' : null;
      if (name === 'cart.update' && url.indexOf('/cart/update.js') !== -1 && !touchesLines(init)) name = null;
      if (name) {
        out.then((res) => {
          try {
            if (!res || !res.ok) return;
            res.clone().json().then((j) => {
              try {
                if (!j) return;
                /* A REFUSED ADD ANSWERS 200 with a body carrying its own
                   status (theme.js, cartBridgeAdd): not a confirmed add. */
                if (j.status && j.status !== 200) return;
                const item = (j.items && j.items[0]) || null;
                /* The cart token is the weld: the order webhook carries the
                   same token, which is how a whole anonymous journey gains
                   its customer. Shopify hands it to the browser with a
                   '?key=...' suffix the webhook's copy never has, so the
                   suffix is stripped here or the weld can never match. */
                const token = j.token ? cap(String(j.token).split('?')[0], 80) : undefined;
                emit(name, {
                  subject: (item && item.handle) || j.handle || undefined,
                  variant_id: (item && item.variant_id) || j.variant_id || undefined,
                  props: {
                    items: typeof j.item_count === 'number' ? j.item_count : undefined,
                    total: typeof j.total_price === 'number' ? j.total_price : undefined,
                    cart_token: token
                  }
                });
                /* /cart/add.js answers with the line added, never the whole
                   cart, so no token ever rides an add - and a shopper who
                   only ever adds would stay unweldable to their order.
                   When the confirmed add carried no token, ask the cart. */
                if (name === 'cart.add' && !token) peekCartToken();
              } catch (err) { /* law 1 */ }
            }).catch(() => { /* not JSON - not a cart answer */ });
          } catch (err) { /* law 1 */ }
        }).catch(() => { /* the page's own error handling owns failures */ });
      }
    } catch (err) { /* the wrap must never cost the page its fetch */ }
    return out;
  };

  /* The one fetch this file ever starts itself: a same-origin /cart.js read
     to fetch the token an add.js answer cannot carry, at most once a minute
     so a burst of adds costs one request. What comes back is emitted as a
     cart.update - same shape the change/update path produces - so the fold
     needs no new vocabulary. It goes through the wrapped window.fetch on
     purpose: '/cart.js' matches none of the three cart-write paths above,
     so the wrap waves it through without recursing. */
  let cartPeekAt = 0;
  function peekCartToken() {
    try {
      /* The one request this file makes for its own sake, so it is the one
         request that must not happen for somebody who has not agreed to be
         measured. Nothing leaves the origin either way, but a fetch fired
         purely to enrich a record we are not allowed to keep is work done
         against the shopper rather than for them. */
      if (!granted) return;
      const now = Date.now();
      if (now - cartPeekAt < 60000) return;
      cartPeekAt = now;
      window.fetch('/cart.js', { credentials: 'same-origin' }).then((res) => {
        try {
          if (!res || !res.ok) return;
          res.json().then((j) => {
            try {
              if (!j || !j.token) return;
              emit('cart.update', {
                props: {
                  items: typeof j.item_count === 'number' ? j.item_count : undefined,
                  total: typeof j.total_price === 'number' ? j.total_price : undefined,
                  cart_token: cap(String(j.token).split('?')[0], 80),
                  /* the shopper changed nothing: this is the token being
                     fetched after an add (2026-09-22, lane 8). A count of
                     cart edits leaves these out. */
                  peek: true
                }
              });
            } catch (err) { /* law 1 */ }
          }).catch(() => { /* not JSON - nothing to weld */ });
        } catch (err) { /* law 1 */ }
      }).catch(() => { /* the weld just waits for another chance */ });
    } catch (err) { /* law 1 */ }
  }

  /* ---- the public surface ----
     track for a named moment, page for a surface that repaints without
     navigating, identify to state who this is (stated-only; it rides the
     next envelope out, nothing is sent for it eagerly). Reserved event
     fields may ride in the props bag and are lifted out here, so feature
     code only ever thinks about one argument. */
  window.loom = {
    track(rawName, props) {
      try {
        const name = validName(rawName);
        if (!name) return;
        const p = (props && typeof props === 'object') ? Object.assign({}, props) : {};
        const o = { props: p };
        if (p.subject != null) { o.subject = p.subject; delete p.subject; }
        if (p.variant_id != null) { o.variant_id = p.variant_id; delete p.variant_id; }
        if (p.value_cents != null) { o.value_cents = p.value_cents; delete p.value_cents; }
        emit(name, o);
      } catch (err) { /* law 1 */ }
    },
    page() {
      /* A surface that repainted without navigating is a new pageview, so it
         gets a new click sequence and a fresh scroll clock. Without this a
         soft repaint appended its presses to the previous page's flow. */
      try { newPageview(); pageView({}); } catch (err) { /* law 1 */ }
    },
    identify(c) {
      try {
        if (!c || typeof c !== 'object') return;
        stated = {
          email: typeof c.email === 'string' ? cap(c.email, 200) : null,
          signed_in: !!c.signed_in
        };
      } catch (err) { /* law 1 */ }
    }
  };

  /* ---- the landing ----
     One page.view per real navigation, referrer attached HERE only - it
     describes how this landing happened, not every event after it - and a
     product.view when the path says this is a PDP. */
  pageView({ ref: document.referrer || undefined });
  /* The read clock starts with the landing, not with the first scroll: a page
     shorter than the viewport is read to the end without anybody touching a
     wheel, and only a measurement that runs on arrival can say so. */
  scrollStart();
  /* An account page view is its own question - "did they go and look at
     their orders" - and it was reserved in the vocabulary, mapped in the
     fold's browse family, and never emitted. The path is the signal; the
     signed-in flag on the page view says which kind of visit it was. */
  if (location.pathname.indexOf('/account') === 0) {
    const c = window.theme && window.theme.customer;
    emit('account.view', {
      subject: location.pathname.split('/')[2] || 'index',
      props: { signed_in: !!(c && c.signed_in) }
    });
  }
  const handle = pathHandle();
  if (handle) {
    let variant;
    try { variant = new URLSearchParams(location.search).get('variant') || undefined; }
    catch (err) { variant = undefined; }
    emit('product.view', { subject: handle, variant_id: variant, props: {} });
  }

  /* Whatever a page script asked of the prerender-time stub happens now,
     in order, against the real API. */
  for (const call of pending) {
    try {
      if (call[0] === 'track') window.loom.track(call[1], call[2]);
      else if (call[0] === 'page') window.loom.page();
      else if (call[0] === 'identify') window.loom.identify(call[1]);
    } catch (err) { /* law 1 */ }
  }

  /* Leftovers from a previous page that never flushed are already in the
     queue; make sure they get their ride even on a quiet page. */
  if (q.length) armTimer();

  /* ---- THE GATE MOVES, AND CAPTURE MOVES WITH IT ----
     One listener, both directions, and it is the ONLY thing in this file that
     can change `granted`. juce-consent.js dispatches this once at boot (so a
     returning shopper's answer arrives on the same tick as everything else)
     and again on every button that changes the answer.

     The yes path is the interesting one. The receipt has already been
     acknowledged by the time this fires - the gate waits for it deliberately,
     because the server refuses ingest it has no consent record for - so by
     now the door can be opened and the buffer emptied in the same breath.
     `detail.vid` is the canonical visitor id the server minted while
     recording the receipt; adopting it here is what stops this browser from
     starting a second thread one flush later. */
  document.addEventListener('juce:consent', (e) => {
    try {
      const detail = (e && e.detail) || {};
      const grants = Array.isArray(detail.grants) ? detail.grants : [];
      const wasGranted = granted;
      /* Which silence the tally so far belongs to, asked before the answer
         moves it: a no given mid-page files what came before it as unasked. */
      const wasLane = laneNow();
      /* `presumed` is analytics measured on notice, without an answer
         (juce-consent.js announce). It opens capture exactly as a yes does;
         identity still waits for a real personalisation grant. */
      const presumed = Array.isArray(detail.presumed) ? detail.presumed : [];
      granted = grants.indexOf('analytics') !== -1 || presumed.indexOf('analytics') !== -1;
      personal = grants.indexOf('personalisation') !== -1;
      /* An event arriving here IS a decision - juce-consent.js dispatches it
         from decide(), from withdraw() and once at boot carrying the standing
         answer - so a grantless one is a recorded no rather than a silence.
         The exceptions are 'undecided' (nobody has been asked) and 'signal'
         (a standing no that outranks the banner, held separately by `refused`
         inside flushAnon). Neither opens the counter. */
      declined = !granted && detail.state !== 'undecided' && detail.state !== 'signal';
      if (granted) { tally = Object.create(null); tallyN = 0; }
      else if (wasLane && wasLane !== laneNow()) flushAnon(false, wasLane);

      /* THE PRESENCE LANE HANDS OVER. Measured from here, so the passer on
         the wall goes now and the same person arrives through the measured
         lane with the buffer below. The tab id goes with it. */
      if (granted && hereTok) {
        try { hereSend({ v: 1, t: hereTok.t, k: 'gone', p: template() }); } catch (err) { }
        try { sessionStorage.removeItem(HERE_KEY); } catch (err) { }
        hereTok = null;
        if (hereTimer) { clearInterval(hereTimer); hereTimer = 0; }
      }

      if (granted && !wasGranted) {
        if (typeof detail.vid === 'string' && detail.vid) state.aid = detail.vid;
        writeState();
        mirror();
        if (q.length) { flush(false); armTimer(); }
        return;
      }
      if (!granted && wasGranted) {
        /* A WITHDRAWAL IS NOT A PAUSE. The queue is dropped rather than held,
           because holding it would mean the next yes silently posts events
           captured under a consent that had already been taken back; and the
           stored identity goes with it, so the thread this browser resumes
           later is genuinely a new one. The server deletes its half on the
           same receipt. */
        q = [];
        purge();
        if (timer) { clearInterval(timer); timer = 0; }
        /* No longer measured, still on the site: back on the wall as a
           passer, with a fresh tab id that shares nothing with the thread
           that was just deleted. */
        here('page', pageSlug());
      }
    } catch (err) { /* law 1 */ }
  });
}
